import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
  TabContent,
  TabPane,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import SimpleBar from "simplebar-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import "flatpickr/dist/plugins/monthSelect/style.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import baconLogo from "../../../assets/images/baconappen.png";
import { formatHeaderDate, resolveUsuarioActual } from "./ControlStock/formatters";
import CollectionsModal from "./ControlStock/components/CollectionsModal";
import {
  createMonthSelectPlugin,
  createWeekSelectPlugin,
  ensureDateValue,
  formatISODate,
  formatMonthYear,
  getISOWeekString,
  getMonthAnchorDate,
  getMonthRangeFromValue,
  getWeekAnchorDate,
  getWeekRangeFromValue,
  normalizeCustomRange,
} from "./ControlStock/dateUtils";

type ConsumoViewMode =
  | "Por Hora"
  | "Por Plataforma"
  | "Por Marca"
  | "Por Sucursal"
  | "Por Día semana"
  | "Por Plan venta";
type ConsumoDimensionMode = Exclude<ConsumoViewMode, "Por Hora">;

const CONSUMO_VIEW_MODE_OPTIONS: ConsumoViewMode[] = [
  "Por Hora",
  "Por Día semana",
  "Por Sucursal",
  "Por Plataforma",
  "Por Plan venta",
  "Por Marca",
];

const CONSUMO_DIMENSION_ENDPOINT: Record<ConsumoDimensionMode, string> = {
  "Por Plataforma": "/api/informes/ventas/consumo/plataforma",
  "Por Marca": "/api/informes/ventas/consumo/marca",
  "Por Sucursal": "/api/informes/ventas/consumo/sucursal",
  "Por Día semana": "/api/informes/ventas/consumo/dia-semana",
  "Por Plan venta": "/api/informes/ventas/consumo/plan-venta",
};

const CONSUMO_DIMENSION_EMPTY_LABEL: Record<ConsumoDimensionMode, string> = {
  "Por Plataforma": "SIN ORIGEN",
  "Por Marca": "SIN MARCA",
  "Por Sucursal": "SIN SUCURSAL",
  "Por Día semana": "SIN DIA",
  "Por Plan venta": "SIN PLAN DE VENTA",
};

const CONSUMO_VIEW_DESCRIPTION: Record<ConsumoViewMode, string> = {
  "Por Hora": "Consumo de ingredientes por hora según ventas registradas.",
  "Por Plataforma":
    "Consumo de ingredientes agrupado por plataforma de venta (origen).",
  "Por Marca": "Consumo de ingredientes agrupado por marca de venta.",
  "Por Sucursal": "Consumo de ingredientes agrupado por sucursal.",
  "Por Día semana": "Consumo de ingredientes agrupado por día de la semana.",
  "Por Plan venta": "Consumo de ingredientes agrupado por plan de venta.",
};

const CONSUMO_LOADING_LABEL: Record<ConsumoDimensionMode, string> = {
  "Por Plataforma": "plataforma",
  "Por Marca": "marca",
  "Por Sucursal": "sucursal",
  "Por Día semana": "día de la semana",
  "Por Plan venta": "plan de venta",
};

type ConsumoDetalleApiMode =
  | "hora"
  | "plataforma"
  | "marca"
  | "sucursal"
  | "dia_semana"
  | "plan_venta";

const CONSUMO_DETAIL_MODE_BY_VIEW: Record<ConsumoViewMode, ConsumoDetalleApiMode> =
  {
    "Por Hora": "hora",
    "Por Plataforma": "plataforma",
    "Por Marca": "marca",
    "Por Sucursal": "sucursal",
    "Por Día semana": "dia_semana",
    "Por Plan venta": "plan_venta",
  };

type ServidoresOfflineResponse = {
  servidores?: string[];
};

type ComparativoAnualFiltrosResponse = {
  sucursales?: string[];
};

type DateMode = "day" | "week" | "month" | "range";

type DateSelectionState = {
  mode: DateMode;
  day: string;
  week: string;
  month: string;
  rangeFrom: string;
  rangeTo: string;
};

type MetricMode = "cantidad" | "monto" | "ambos";
type ConsumoExportTarget = ConsumoViewMode | "all";

type ConsumoPorHoraRow = {
  serverOrigen: string;
  idArticulo: string | null;
  articulo: string | null;
  familia: string | null;
  linea: string | null;
  marca: string | null;
  hora00: number;
  hora00Monto: number;
  hora01: number;
  hora01Monto: number;
  hora02: number;
  hora02Monto: number;
  hora03: number;
  hora03Monto: number;
  hora04: number;
  hora04Monto: number;
  hora05: number;
  hora05Monto: number;
  hora06: number;
  hora06Monto: number;
  hora07: number;
  hora07Monto: number;
  hora08: number;
  hora08Monto: number;
  hora09: number;
  hora09Monto: number;
  hora10: number;
  hora10Monto: number;
  hora11: number;
  hora11Monto: number;
  hora12: number;
  hora12Monto: number;
  hora13: number;
  hora13Monto: number;
  hora14: number;
  hora14Monto: number;
  hora15: number;
  hora15Monto: number;
  hora16: number;
  hora16Monto: number;
  hora17: number;
  hora17Monto: number;
  hora18: number;
  hora18Monto: number;
  hora19: number;
  hora19Monto: number;
  hora20: number;
  hora20Monto: number;
  hora21: number;
  hora21Monto: number;
  hora22: number;
  hora22Monto: number;
  hora23: number;
  hora23Monto: number;
  sinHora: number;
  sinHoraMonto: number;
  total: number;
  totalMonto: number;
};

type ConsumoPorDimensionRow = {
  serverOrigen: string;
  idArticulo: string | null;
  articulo: string | null;
  familia: string | null;
  linea: string | null;
  marca: string | null;
  dimension: string;
  cantidad: number;
  monto: number;
};

type HourKey =
  | "hora00"
  | "hora01"
  | "hora02"
  | "hora03"
  | "hora04"
  | "hora05"
  | "hora06"
  | "hora07"
  | "hora08"
  | "hora09"
  | "hora10"
  | "hora11"
  | "hora12"
  | "hora13"
  | "hora14"
  | "hora15"
  | "hora16"
  | "hora17"
  | "hora18"
  | "hora19"
  | "hora20"
  | "hora21"
  | "hora22"
  | "hora23";

type HourAmountKey =
  | "hora00Monto"
  | "hora01Monto"
  | "hora02Monto"
  | "hora03Monto"
  | "hora04Monto"
  | "hora05Monto"
  | "hora06Monto"
  | "hora07Monto"
  | "hora08Monto"
  | "hora09Monto"
  | "hora10Monto"
  | "hora11Monto"
  | "hora12Monto"
  | "hora13Monto"
  | "hora14Monto"
  | "hora15Monto"
  | "hora16Monto"
  | "hora17Monto"
  | "hora18Monto"
  | "hora19Monto"
  | "hora20Monto"
  | "hora21Monto"
  | "hora22Monto"
  | "hora23Monto";

type EmptyHourRange = {
  rangeKey: string;
  keys: HourKey[];
  label: string;
};

type HourDisplayColumn =
  | {
      type: "hour";
      key: HourKey;
      label: string;
      rangeKey?: string;
      rangeLabel?: string;
      isRangeStart?: boolean;
    }
  | {
      type: "collapsed";
      rangeKey: string;
      keys: HourKey[];
      label: string;
    };

type ActionsModalTab = {
  key: string;
  label: string;
  filter:
    | { type: "hour"; from: number; to: number }
    | { type: "dimension"; value: string };
};

type ConsumoDetalleRow = {
  serverOrigen: string;
  idIngrediente: string | null;
  ingrediente: string | null;
  idArticuloVenta: string | null;
  articuloVenta: string | null;
  cantidadVendida: number;
  factorReceta: number;
  cantidadConsumida: number;
  montoConsumido: number;
  origenCalculo: "RECETA" | "DIRECTO";
  cantidadMovimientos: number;
  tickets: TicketModalRow[];
};

type TicketModalRow = {
  idMovimiento: number | null;
  ticket: string;
  fecha: string | null;
  hora: string | null;
  sucursal: string | null;
  cliente: string | null;
  rucDoc: string | null;
  origen: string | null;
};

type ConsumoTicketReconstruidoResponse = {
  cabecera: {
    serverOrigen: string;
    idMovimiento: number;
    ticket: string;
    fecha: string | null;
    hora: string | null;
    idSucursal: number | null;
    sucursal: string | null;
    deposito: string | null;
    origen: string | null;
    marca: string | null;
    planVenta: string | null;
    persona: string | null;
    ruc: string | null;
    documentoIdentidad: string | null;
  };
  totales: {
    items: number;
    cantidadMovimiento: number;
    cantidadComanda: number;
    totalImporte: number;
    totalCostoSinIva: number;
    totalCostoConIva: number;
  };
  detalle: Array<{
    idArticulo: string | null;
    articulo: string | null;
    idArticuloPadre: string | null;
    articuloPadre: string | null;
    cantidadMovimiento: number;
    cantidadComanda: number;
    diferenciaCantidad: number;
    precioUnitario: number;
    totalImporte: number;
    costoUnitSinIva: number;
    costoUnitConIva: number;
    costoTotalSinIva: number;
    costoTotalConIva: number;
    estadoRelacion: "RELACIONADO" | "SOLO_MOVIMIENTO" | "SOLO_COMANDA";
  }>;
};

type ConsumoDetalleTotals = {
  cantidadVendida: number;
  cantidadConsumida: number;
  montoConsumido: number;
  cantidadMovimientos: number;
};

type HourTotals = Record<HourKey, number> &
  Record<HourAmountKey, number> & {
    sinHora: number;
    sinHoraMonto: number;
    total: number;
    totalMonto: number;
  };

type ConsumoDimensionMatrixRow = {
  idArticulo: string | null;
  articulo: string;
  values: Record<string, { cantidad: number; monto: number }>;
  total: number;
  totalMonto: number;
};

type ConsumoExportHourDataset = {
  kind: "hour";
  mode: "Por Hora";
  rows: ConsumoPorHoraRow[];
  totals: HourTotals;
};

type ConsumoExportDimensionDataset = {
  kind: "dimension";
  mode: ConsumoDimensionMode;
  rows: ConsumoDimensionMatrixRow[];
  columns: string[];
  totalsByDimension: Record<string, { cantidad: number; monto: number }>;
  grandTotal: number;
  grandTotalMonto: number;
};

type ConsumoExportDataset = ConsumoExportHourDataset | ConsumoExportDimensionDataset;

type CollectionGroup = {
  id: number;
  nombre: string;
  articulos: Set<string>;
};

const HOUR_KEYS: HourKey[] = [
  "hora00",
  "hora01",
  "hora02",
  "hora03",
  "hora04",
  "hora05",
  "hora06",
  "hora07",
  "hora08",
  "hora09",
  "hora10",
  "hora11",
  "hora12",
  "hora13",
  "hora14",
  "hora15",
  "hora16",
  "hora17",
  "hora18",
  "hora19",
  "hora20",
  "hora21",
  "hora22",
  "hora23",
];

const HOUR_AMOUNT_KEYS: HourAmountKey[] = [
  "hora00Monto",
  "hora01Monto",
  "hora02Monto",
  "hora03Monto",
  "hora04Monto",
  "hora05Monto",
  "hora06Monto",
  "hora07Monto",
  "hora08Monto",
  "hora09Monto",
  "hora10Monto",
  "hora11Monto",
  "hora12Monto",
  "hora13Monto",
  "hora14Monto",
  "hora15Monto",
  "hora16Monto",
  "hora17Monto",
  "hora18Monto",
  "hora19Monto",
  "hora20Monto",
  "hora21Monto",
  "hora22Monto",
  "hora23Monto",
];

const HOUR_TO_AMOUNT_KEY: Record<HourKey, HourAmountKey> = {
  hora00: "hora00Monto",
  hora01: "hora01Monto",
  hora02: "hora02Monto",
  hora03: "hora03Monto",
  hora04: "hora04Monto",
  hora05: "hora05Monto",
  hora06: "hora06Monto",
  hora07: "hora07Monto",
  hora08: "hora08Monto",
  hora09: "hora09Monto",
  hora10: "hora10Monto",
  hora11: "hora11Monto",
  hora12: "hora12Monto",
  hora13: "hora13Monto",
  hora14: "hora14Monto",
  hora15: "hora15Monto",
  hora16: "hora16Monto",
  hora17: "hora17Monto",
  hora18: "hora18Monto",
  hora19: "hora19Monto",
  hora20: "hora20Monto",
  hora21: "hora21Monto",
  hora22: "hora22Monto",
  hora23: "hora23Monto",
};

const NULL_COLLECTION_VALUE = "__sin_coleccion__";

const getHourLabel = (key: HourKey) => key.slice(4);

const DATE_MODE_OPTIONS: Array<{
  key: DateMode;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    key: "day",
    label: "Día",
    description: "Filtrar por un único día",
    icon: "ri-calendar-line",
  },
  {
    key: "week",
    label: "Semana",
    description: "Filtrar por semana calendario ISO",
    icon: "ri-calendar-line",
  },
  {
    key: "month",
    label: "Mes",
    description: "Filtrar por un mes completo",
    icon: "ri-calendar-event-line",
  },
  {
    key: "range",
    label: "Rango",
    description: "Selecciona una fecha de inicio y fin",
    icon: "ri-calendar-check-line",
  },
];

const DATE_MODE_MAP = DATE_MODE_OPTIONS.reduce<
  Record<DateMode, (typeof DATE_MODE_OPTIONS)[number]>
>((acc, option) => {
  acc[option.key] = option;
  return acc;
}, {} as Record<DateMode, (typeof DATE_MODE_OPTIONS)[number]>);

const getCurrentDate = () => formatISODate(new Date());

const parseDateForPicker = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const numberFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const formatNumber = (value: number) => numberFormat.format(value ?? 0);
const formatCost = (value: number) => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) {
    return "0";
  }
  const rounded = Math.round(numeric);
  const sign = rounded < 0 ? "-" : "";
  const abs = Math.abs(rounded);
  return `${sign}${abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

const formatCostCompact = (value: number) => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) {
    return "0";
  }
  const rounded = Math.round(numeric);
  const abs = Math.abs(rounded);
  if (abs < 1_000_000) {
    return formatCost(rounded);
  }
  const full = formatCost(rounded);
  const negative = full.startsWith("-");
  const raw = negative ? full.slice(1) : full;
  const parts = raw.split(".");
  const compactCore = parts.slice(0, 2).join(".");
  return `${negative ? "-" : ""}${compactCore}..`;
};

const INGREDIENT_COLUMN_STYLE: React.CSSProperties = {
  width: 150,
  minWidth: 150,
  maxWidth: 150,
};

const INGREDIENT_COLUMN_STYLE_HOUR: React.CSSProperties = {
  width: 230,
  minWidth: 230,
  maxWidth: 230,
};

const INGREDIENT_CELL_STYLE: React.CSSProperties = {
  width: 150,
  minWidth: 150,
  maxWidth: 150,
  whiteSpace: "normal",
  wordBreak: "break-word",
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
  lineHeight: 1.08,
};

const INGREDIENT_CELL_STYLE_HOUR: React.CSSProperties = {
  ...INGREDIENT_CELL_STYLE,
  width: 230,
  minWidth: 230,
  maxWidth: 230,
};

const HOUR_COLUMN_STYLE: React.CSSProperties = {
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
};

const HOUR_CELL_STYLE: React.CSSProperties = {
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
  lineHeight: 1.04,
};

const COLLAPSED_HOUR_HEADER_STYLE: React.CSSProperties = {
  width: 110,
  minWidth: 110,
  maxWidth: 110,
  padding: "0.42rem 0.55rem",
  textAlign: "center",
};

const COLLAPSED_HOUR_CELL_STYLE: React.CSSProperties = {
  width: 110,
  minWidth: 110,
  maxWidth: 110,
  padding: "0.42rem 0.55rem",
  textAlign: "center",
};

const buildEmptyHoursLegend = (label: string) => {
  const [from, to] = label.split("-");
  if (from && to) {
    return `Sin datos ${from}-${to}`;
  }
  return `Sin datos ${label}`;
};

const SUMMARY_COLUMN_STYLE: React.CSSProperties = {
  width: 68,
  minWidth: 68,
  maxWidth: 68,
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
};

const SUMMARY_CELL_STYLE: React.CSSProperties = {
  width: 68,
  minWidth: 68,
  maxWidth: 68,
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
};

const ACTION_COLUMN_STYLE: React.CSSProperties = {
  width: 54,
  minWidth: 54,
  maxWidth: 54,
  padding: "0.42rem 0.55rem",
  textAlign: "center",
};

const ACTION_CELL_STYLE: React.CSSProperties = {
  width: 54,
  minWidth: 54,
  maxWidth: 54,
  padding: "0.42rem 0.55rem",
  textAlign: "center",
};

const MODAL_DETAIL_HEADER_STYLE: React.CSSProperties = {
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
  lineHeight: 1.05,
  whiteSpace: "nowrap",
};

const MODAL_DETAIL_CELL_STYLE: React.CSSProperties = {
  padding: "0.42rem 0.55rem",
  fontSize: "0.925rem",
  lineHeight: 1.06,
};

const MODAL_DETAIL_ARTICLE_CELL_STYLE: React.CSSProperties = {
  ...MODAL_DETAIL_CELL_STYLE,
  minWidth: 220,
};

const DARK_ACTIVE_VIEW_MODE_STYLE: React.CSSProperties = {
  backgroundColor: "rgba(var(--bs-primary-rgb), 0.28)",
  borderColor: "rgba(var(--bs-primary-rgb), 0.78)",
  color: "#fff",
  boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.12)",
};

const normalizeSearchText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const truncateWithDots = (value: string, maxLength: number) => {
  const text = String(value ?? "").trim();
  if (text.length <= maxLength) {
    return text;
  }
  const safeLen = Math.max(1, maxLength - 2);
  return `${text.slice(0, safeLen).trimEnd()}..`;
};

const getDayOfWeekRank = (value: string) => {
  const normalized = normalizeSearchText(value).toUpperCase();
  const ranks: Record<string, number> = {
    LUNES: 1,
    MARTES: 2,
    MIERCOLES: 3,
    JUEVES: 4,
    VIERNES: 5,
    SABADO: 6,
    DOMINGO: 7,
  };
  return ranks[normalized] ?? 999;
};

const VentasConsumoOfflinePage = () => {
  const [viewMode, setViewMode] = useState<ConsumoViewMode>("Por Hora");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    if (typeof document === "undefined") return false;
    const htmlTheme = document.documentElement.getAttribute("data-bs-theme");
    const bodyTheme = document.body.getAttribute("data-bs-theme");
    const layoutMode = document.documentElement.getAttribute("data-layout-mode");
    return htmlTheme === "dark" || bodyTheme === "dark" || layoutMode === "dark";
  });

  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [serverSeleccionado, setServerSeleccionado] = useState<string>("");
  const [serverLoading, setServerLoading] = useState(false);

  const [dateSelection, setDateSelection] = useState<DateSelectionState>(() => {
    const today = getCurrentDate();
    const todayDate = parseDateForPicker(today) ?? new Date();
    return {
      mode: "month",
      day: today,
      week: getISOWeekString(todayDate),
      month: today.slice(0, 7),
      rangeFrom: today,
      rangeTo: today,
    };
  });
  const [dateModeDropdownOpen, setDateModeDropdownOpen] = useState(false);
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [metricMode, setMetricMode] = useState<MetricMode>("cantidad");
  const [expandedEmptyHourRanges, setExpandedEmptyHourRanges] = useState<
    Record<string, boolean>
  >({});
  const [sucursalOptions, setSucursalOptions] = useState<string[]>([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState<string>("");

  const [rows, setRows] = useState<ConsumoPorHoraRow[]>([]);
  const [platformRows, setPlatformRows] = useState<ConsumoPorDimensionRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [actionsModalTabs, setActionsModalTabs] = useState<ActionsModalTab[]>([]);
  const [actionsModalActiveTab, setActionsModalActiveTab] = useState("");
  const [actionsModalIngredientId, setActionsModalIngredientId] = useState<string>("");
  const [actionsModalIngredient, setActionsModalIngredient] = useState("");
  const [actionsModalViewMode, setActionsModalViewMode] =
    useState<ConsumoViewMode>("Por Hora");
  const [actionsModalLoadingByTab, setActionsModalLoadingByTab] = useState<
    Record<string, boolean>
  >({});
  const [actionsModalErrorByTab, setActionsModalErrorByTab] = useState<
    Record<string, string | null>
  >({});
  const [actionsModalRowsByTab, setActionsModalRowsByTab] = useState<
    Record<string, ConsumoDetalleRow[]>
  >({});
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketModalTitle, setTicketModalTitle] = useState("");
  const [ticketModalRows, setTicketModalRows] = useState<TicketModalRow[]>([]);
  const [ticketDetailModalOpen, setTicketDetailModalOpen] = useState(false);
  const [ticketDetailModalTitle, setTicketDetailModalTitle] = useState("");
  const [ticketDetailModalLoading, setTicketDetailModalLoading] = useState(false);
  const [ticketDetailModalError, setTicketDetailModalError] = useState<string | null>(
    null,
  );
  const [ticketDetailModalData, setTicketDetailModalData] =
    useState<ConsumoTicketReconstruidoResponse | null>(null);
  const [isCollectionsModalOpen, setIsCollectionsModalOpen] = useState(false);
  const [groupViewMode, setGroupViewMode] = useState<"sin" | "coleccion">("coleccion");
  const [collectionFilter, setCollectionFilter] = useState("todas");
  const [collectionGroups, setCollectionGroups] = useState<CollectionGroup[]>([]);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  const permisosUsuario = useMemo(() => {
    const raw =
      sessionStorage.getItem("authUser") ||
      localStorage.getItem("authUser") ||
      sessionStorage.getItem("user") ||
      localStorage.getItem("user");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      const permisos =
        parsed?.usuario?.permisos ??
        parsed?.permisos ??
        parsed?.user?.permisos ??
        parsed?.data?.usuario?.permisos ??
        [];
      return Array.isArray(permisos) ? permisos : [];
    } catch {
      return [];
    }
  }, []);

  const canViewCollections =
    permisosUsuario.includes("informes.colecciones.ver") ||
    permisosUsuario.includes("general.solo_admin");
  const canEditCollections =
    permisosUsuario.includes("informes.colecciones.editar") ||
    permisosUsuario.includes("general.solo_admin");
  const canCloneCollections =
    permisosUsuario.includes("informes.colecciones.clonar") ||
    permisosUsuario.includes("general.solo_admin");

  const currentModeOption = DATE_MODE_MAP[dateSelection.mode];

  const dateFilter = useMemo(() => {
    const today = getCurrentDate();
    if (dateSelection.mode === "day") {
      const dayValue = ensureDateValue(dateSelection.day, today);
      return {
        periodo: dayValue,
        fechaDesde: dayValue,
        fechaHasta: dayValue,
        label: formatHeaderDate(dayValue),
      };
    }
    if (dateSelection.mode === "week") {
      const { start, end } = getWeekRangeFromValue(dateSelection.week, today);
      return {
        periodo: start.slice(0, 7),
        fechaDesde: start,
        fechaHasta: end,
        label: `${formatHeaderDate(start)} - ${formatHeaderDate(end)}`,
      };
    }
    if (dateSelection.mode === "month") {
      const { start, end } = getMonthRangeFromValue(dateSelection.month, today);
      return {
        periodo: dateSelection.month,
        fechaDesde: start,
        fechaHasta: end,
        label:
          formatMonthYear(dateSelection.month) ||
          `${formatHeaderDate(start)} - ${formatHeaderDate(end)}`,
      };
    }

    const { start, end } = normalizeCustomRange(
      dateSelection.rangeFrom,
      dateSelection.rangeTo,
      today,
    );
    return {
      periodo: start.slice(0, 7),
      fechaDesde: start,
      fechaHasta: end,
      label: `${formatHeaderDate(start)} - ${formatHeaderDate(end)}`,
    };
  }, [dateSelection]);

  const updateDateSelection = useCallback(
    (updates: Partial<DateSelectionState>) => {
      setDateSelection((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const handleModeChange = useCallback((mode: DateMode) => {
    setDateSelection((prev) => {
      if (prev.mode === mode) return prev;
      const today = getCurrentDate();
      const next: DateSelectionState = { ...prev, mode };
      if (mode === "day" && !prev.day) {
        next.day = today;
      }
      if (mode === "week" && !prev.week) {
        next.week = getISOWeekString(parseDateForPicker(today) ?? new Date());
      }
      if (mode === "month" && !prev.month) {
        next.month = today.slice(0, 7);
      }
      if (mode === "range") {
        if (!prev.rangeFrom) {
          next.rangeFrom = today;
        }
        if (!prev.rangeTo) {
          next.rangeTo = today;
        }
      }
      return next;
    });
    setDateModeDropdownOpen(false);
    setIsSelectingRange(false);
  }, []);

  const toggleDateModeDropdown = useCallback(
    () => setDateModeDropdownOpen((prev) => !prev),
    [],
  );

  const renderDateModeInput = useCallback(() => {
    if (dateSelection.mode === "range") {
      const startDate = parseDateForPicker(dateSelection.rangeFrom);
      const hasDistinctRange =
        dateSelection.rangeTo !== dateSelection.rangeFrom &&
        dateSelection.rangeTo.trim().length > 0;
      const endDate = hasDistinctRange
        ? parseDateForPicker(dateSelection.rangeTo)
        : null;
      const pickerValues: Date[] =
        !startDate || isSelectingRange
          ? []
          : endDate
            ? [startDate, endDate]
            : [startDate];

      const handleRangeChange = (
        dates: Date[],
        _dateStr: string,
        instance?: FlatpickrInstance,
      ) => {
        if (!dates || dates.length === 0) {
          return;
        }
        const [start, end] = dates;
        if (!start) {
          return;
        }
        const startStr = formatISODate(start);
        if (!end) {
          setIsSelectingRange(true);
          updateDateSelection({ rangeFrom: startStr, rangeTo: startStr });
          if (instance && instance.isOpen) {
            window.setTimeout(() => {
              try {
                instance.open();
              } catch {
                // noop
              }
            }, 0);
          }
          return;
        }
        setIsSelectingRange(false);
        const endStr = formatISODate(end);
        const normalized = normalizeCustomRange(startStr, endStr, startStr);
        updateDateSelection({
          rangeFrom: normalized.start,
          rangeTo: normalized.end,
        });
      };

      return (
        <div className="flex-grow-1">
          <Flatpickr
            className="form-control rounded-end flag-input"
            value={pickerValues.length > 0 ? pickerValues : undefined}
            options={{ mode: "range", dateFormat: "Y-m-d", closeOnSelect: false }}
            onChange={handleRangeChange}
          />
        </div>
      );
    }

    if (dateSelection.mode === "week") {
      const today = getCurrentDate();
      const weekDate = getWeekAnchorDate(dateSelection.week, today);
      return (
        <Flatpickr
          className="form-control rounded-end flag-input"
          value={weekDate ? [weekDate] : undefined}
          options={{
            mode: "single",
            dateFormat: "Y-m-d",
            weekNumbers: true,
            plugins: [createWeekSelectPlugin()],
          }}
          onChange={(dates: Date[]) => {
            if (!dates || dates.length === 0 || !dates[0]) {
              return;
            }
            updateDateSelection({ week: getISOWeekString(dates[0]) });
          }}
        />
      );
    }

    if (dateSelection.mode === "month") {
      const today = getCurrentDate();
      const monthDate = getMonthAnchorDate(dateSelection.month, today);
      return (
        <Flatpickr
          className="form-control rounded-end flag-input"
          value={monthDate ? [monthDate] : undefined}
          options={{
            mode: "single",
            altInput: true,
            plugins: [
              createMonthSelectPlugin({
                shorthand: true,
                dateFormat: "Y-m",
                altFormat: "F Y",
              }),
            ],
          }}
          onChange={(dates: Date[]) => {
            if (!dates || dates.length === 0 || !dates[0]) {
              return;
            }
            const selected = dates[0];
            const value = `${selected.getFullYear()}-${String(
              selected.getMonth() + 1,
            ).padStart(2, "0")}`;
            updateDateSelection({ month: value });
          }}
        />
      );
    }

    return (
      <Input
        type="date"
        className="form-control rounded-end flag-input"
        value={dateSelection.day}
        onChange={(event) => updateDateSelection({ day: event.target.value })}
        style={{ minWidth: 0 }}
      />
    );
  }, [dateSelection, isSelectingRange, updateDateSelection]);

  const loadServerOptions = useCallback(async () => {
    setServerLoading(true);
    try {
      const data = await requestJson<ServidoresOfflineResponse>(
        "/api/informes/ventas/comparativo-offline/filtros",
      );
      const options = Array.from(
        new Set(
          (data?.servidores ?? [])
            .map((item) => item.trim().toUpperCase())
            .filter((item) => item.length > 0),
        ),
      );
      setServerOptions(options);
      setServerSeleccionado((prev) => {
        if (prev && options.includes(prev)) return prev;
        if (options.includes("PARLA_SERVER")) return "PARLA_SERVER";
        return options[0] ?? "";
      });
    } catch (err) {
      console.error("No se pudieron cargar los servidores:", err);
      setServerOptions([]);
      setError("No se pudieron cargar los servidores offline.");
    } finally {
      setServerLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadServerOptions();
  }, [loadServerOptions]);

  useEffect(() => {
    let active = true;
    const loadLogo = async () => {
      try {
        const sources = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const source of sources) {
          if (!source) continue;
          try {
            const response = await fetch(source);
            if (!response.ok) continue;
            const blob = await response.blob();
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                if (typeof reader.result === "string") {
                  resolve(reader.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              reader.onerror = () => reject(new Error("No pudimos leer el logo."));
              reader.readAsDataURL(blob);
            });
            if (active) {
              setLogoDataUrl(dataUrl);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // noop
      }
    };
    void loadLogo();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const evaluateTheme = () => {
      const htmlTheme = document.documentElement.getAttribute("data-bs-theme");
      const bodyTheme = document.body.getAttribute("data-bs-theme");
      const layoutMode = document.documentElement.getAttribute("data-layout-mode");
      setIsDarkTheme(
        htmlTheme === "dark" || bodyTheme === "dark" || layoutMode === "dark",
      );
    };

    evaluateTheme();

    const observer = new MutationObserver(() => evaluateTheme());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme", "data-layout-mode"],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-bs-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!serverSeleccionado) {
      setSucursalOptions([]);
      setSucursalSeleccionada("");
      return;
    }

    let active = true;

    const loadSucursales = async () => {
      try {
        const params = new URLSearchParams({
          server: serverSeleccionado,
          desde: dateFilter.fechaDesde,
          hasta: dateFilter.fechaHasta,
        });
        const data = await requestJson<ComparativoAnualFiltrosResponse>(
          `/api/informes/ventas/comparativo-anual/filtros?${params.toString()}`,
        );
        if (!active) return;
        const options = Array.from(
          new Set(
            (data?.sucursales ?? [])
              .map((item) => item.trim())
              .filter((item) => item.length > 0),
          ),
        );
        setSucursalOptions(options);
        setSucursalSeleccionada((prev) =>
          prev && options.includes(prev) ? prev : "",
        );
      } catch (err) {
        if (!active) return;
        console.error("No se pudieron cargar sucursales:", err);
        setSucursalOptions([]);
        setSucursalSeleccionada("");
      }
    };

    void loadSucursales();
    return () => {
      active = false;
    };
  }, [serverSeleccionado, dateFilter.fechaDesde, dateFilter.fechaHasta]);

  useEffect(() => {
    if (!serverSeleccionado || !dateFilter.periodo) {
      setRows([]);
      setPlatformRows([]);
      return;
    }

    let active = true;

    const loadRows = async () => {
      setLoadingRows(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          server: serverSeleccionado,
          periodo: dateFilter.periodo,
          fechaDesde: dateFilter.fechaDesde,
          fechaHasta: dateFilter.fechaHasta,
        });
        if (sucursalSeleccionada) {
          params.set("sucursal", sucursalSeleccionada);
        }

        if (!active) return;

        if (viewMode === "Por Hora") {
          const data = await requestJson<ConsumoPorHoraRow[]>(
            `/api/informes/ventas/consumo/hora?${params.toString()}`,
          );
          if (!active) return;
          setRows(Array.isArray(data) ? data : []);
          setPlatformRows([]);
        } else {
          const dimensionMode = viewMode as ConsumoDimensionMode;
          const endpoint = CONSUMO_DIMENSION_ENDPOINT[dimensionMode];
          const emptyLabel = CONSUMO_DIMENSION_EMPTY_LABEL[dimensionMode];
          const data = await requestJson<ConsumoPorDimensionRow[]>(
            `${endpoint}?${params.toString()}`,
          );
          if (!active) return;
          setPlatformRows(
            Array.isArray(data)
              ? data.map((row) => {
                  const raw = String(row.dimension ?? "").trim();
                  return {
                    ...row,
                    dimension: raw.length > 0 ? raw : emptyLabel,
                  };
                })
              : [],
          );
          setRows([]);
        }
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar consumo:", err);
        setError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar el informe de consumo.",
        );
        setRows([]);
        setPlatformRows([]);
      } finally {
        if (active) {
          setLoadingRows(false);
        }
      }
    };

    void loadRows();
    return () => {
      active = false;
    };
  }, [
    viewMode,
    serverSeleccionado,
    sucursalSeleccionada,
    dateFilter.periodo,
    dateFilter.fechaDesde,
    dateFilter.fechaHasta,
  ]);

  useEffect(() => {
    if (!canViewCollections) {
      setCollectionGroups([]);
      setCollectionFilter("todas");
      setGroupViewMode("sin");
      return;
    }
    if (isCollectionsModalOpen) {
      return;
    }

    let cancelled = false;

    const loadCollections = async () => {
      try {
        const data = await requestJson("/api/informes/colecciones");
        const colecciones = Array.isArray(data) ? data : [];
        const detailed = await Promise.all(
          colecciones.map(async (coleccion: { id: number; nombre?: string | null }) => {
            const nombre = String(coleccion?.nombre ?? `Colección ${coleccion.id}`);
            try {
              const items = await requestJson(
                `/api/informes/colecciones/${coleccion.id}/articulos`,
              );
              const articulos = new Set(
                Array.isArray(items)
                  ? items
                      .map((id) => String(id ?? "").trim())
                      .filter((id) => id.length > 0)
                  : [],
              );
              return { id: coleccion.id, nombre, articulos } as CollectionGroup;
            } catch {
              return { id: coleccion.id, nombre, articulos: new Set() } as CollectionGroup;
            }
          }),
        );
        if (!cancelled) {
          setCollectionGroups(detailed);
        }
      } catch {
        if (!cancelled) {
          setCollectionGroups([]);
        }
      }
    };

    void loadCollections();
    return () => {
      cancelled = true;
    };
  }, [canViewCollections, isCollectionsModalOpen]);

  const collectionAssignmentMap = useMemo(() => {
    const map = new Map<string, string>();
    collectionGroups.forEach((collection) => {
      collection.articulos.forEach((id) => {
        if (!map.has(id)) {
          map.set(id, String(collection.id));
        }
      });
    });
    return map;
  }, [collectionGroups]);

  const collectionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    collectionGroups.forEach((collection) => {
      map.set(String(collection.id), collection.nombre);
    });
    return map;
  }, [collectionGroups]);

  const selectedCollectionLabel = useMemo(() => {
    if (collectionFilter === "todas") {
      return "Todas las colecciones";
    }
    if (collectionFilter === NULL_COLLECTION_VALUE) {
      return "Sin colección";
    }
    return collectionLabelMap.get(collectionFilter) ?? "Colección";
  }, [collectionFilter, collectionLabelMap]);

  const filteredRows = useMemo(() => {
    const query = normalizeSearchText(searchTerm);
    const tokens = query.split(/\s+/).filter((item) => item.length > 0);

    return rows.filter((row) => {
      const articleId = String(row.idArticulo ?? "").trim();
      const collectionId = articleId ? collectionAssignmentMap.get(articleId) : undefined;
      const collectionLabel = collectionId
        ? collectionLabelMap.get(collectionId) ?? ""
        : "Sin colección";
      const matchesCollection =
        collectionFilter === "todas" ||
        (collectionFilter === NULL_COLLECTION_VALUE
          ? !collectionId
          : collectionId === collectionFilter);
      if (!matchesCollection) {
        return false;
      }

      if (tokens.length === 0) {
        return true;
      }

      const haystack = normalizeSearchText(
        [
          row.articulo ?? "",
          row.idArticulo ?? "",
          row.familia ?? "",
          row.linea ?? "",
          row.marca ?? "",
          collectionLabel,
        ].join(" "),
      );
      return tokens.every((token) => haystack.includes(token));
    });
  }, [rows, searchTerm, collectionAssignmentMap, collectionFilter, collectionLabelMap]);

  const currentDimensionEmptyLabel =
    viewMode === "Por Hora"
      ? "SIN DATOS"
      : CONSUMO_DIMENSION_EMPTY_LABEL[viewMode as ConsumoDimensionMode];

  const filteredPlatformRows = useMemo(() => {
    const query = normalizeSearchText(searchTerm);
    const tokens = query.split(/\s+/).filter((item) => item.length > 0);

    return platformRows.filter((row) => {
      const articleId = String(row.idArticulo ?? "").trim();
      const collectionId = articleId ? collectionAssignmentMap.get(articleId) : undefined;
      const collectionLabel = collectionId
        ? collectionLabelMap.get(collectionId) ?? ""
        : "Sin colección";
      const matchesCollection =
        collectionFilter === "todas" ||
        (collectionFilter === NULL_COLLECTION_VALUE
          ? !collectionId
          : collectionId === collectionFilter);
      if (!matchesCollection) {
        return false;
      }

      if (tokens.length === 0) {
        return true;
      }

      const haystack = normalizeSearchText(
        [
          row.articulo ?? "",
          row.idArticulo ?? "",
          row.familia ?? "",
          row.linea ?? "",
          row.marca ?? "",
          row.dimension ?? "",
          collectionLabel,
        ].join(" "),
      );
      return tokens.every((token) => haystack.includes(token));
    });
  }, [platformRows, searchTerm, collectionAssignmentMap, collectionFilter, collectionLabelMap]);

  const platformColumns = useMemo(() => {
    const unique = new Set<string>();
    filteredPlatformRows.forEach((row) => {
      const dimension = String(row.dimension ?? "").trim();
      unique.add(
        dimension.length > 0 ? dimension : currentDimensionEmptyLabel,
      );
    });
    const values = Array.from(unique);
    if (viewMode === "Por Día semana") {
      return values.sort((a, b) => {
        const rankA = getDayOfWeekRank(a);
        const rankB = getDayOfWeekRank(b);
        if (rankA !== rankB) return rankA - rankB;
        return a.localeCompare(b, "es");
      });
    }
    return values.sort((a, b) => a.localeCompare(b, "es"));
  }, [filteredPlatformRows, currentDimensionEmptyLabel, viewMode]);

  const platformMatrix = useMemo(() => {
    const grouped = new Map<
      string,
      {
        idArticulo: string | null;
        articulo: string;
        values: Record<string, { cantidad: number; monto: number }>;
        total: number;
        totalMonto: number;
      }
    >();

    filteredPlatformRows.forEach((row) => {
      const idArticulo = row.idArticulo ? String(row.idArticulo) : null;
      const articulo = row.articulo ?? idArticulo ?? "Sin ingrediente";
      const groupKey = `${idArticulo ?? ""}::${articulo}`;
      const dimensionRaw = String(row.dimension ?? "").trim();
      const plataforma =
        dimensionRaw.length > 0 ? dimensionRaw : currentDimensionEmptyLabel;
      const cantidad = Number(row.cantidad ?? 0);
      const monto = Number(row.monto ?? 0);

      let current = grouped.get(groupKey);
      if (!current) {
        current = {
          idArticulo,
          articulo,
          values: {},
          total: 0,
          totalMonto: 0,
        };
        grouped.set(groupKey, current);
      }

      const slot = current.values[plataforma] ?? { cantidad: 0, monto: 0 };
      slot.cantidad += cantidad;
      slot.monto += monto;
      current.values[plataforma] = slot;
      current.total += cantidad;
      current.totalMonto += monto;
    });

    const matrixRows = Array.from(grouped.values()).sort((a, b) => {
      const totalDiff = b.total - a.total;
      if (totalDiff !== 0) return totalDiff;
      return a.articulo.localeCompare(b.articulo, "es");
    });

    const totalsByPlatform: Record<string, { cantidad: number; monto: number }> = {};
    platformColumns.forEach((platform) => {
      totalsByPlatform[platform] = { cantidad: 0, monto: 0 };
    });

    let grandTotal = 0;
    let grandTotalMonto = 0;

    matrixRows.forEach((row) => {
      platformColumns.forEach((platform) => {
        const value = row.values[platform] ?? { cantidad: 0, monto: 0 };
        totalsByPlatform[platform].cantidad += value.cantidad;
        totalsByPlatform[platform].monto += value.monto;
      });
      grandTotal += row.total;
      grandTotalMonto += row.totalMonto;
    });

    return {
      rows: matrixRows,
      totalsByPlatform,
      grandTotal,
      grandTotalMonto,
    };
  }, [filteredPlatformRows, platformColumns, currentDimensionEmptyLabel]);

  const platformUniqueTotal = useMemo(() => {
    const keys = new Set(
      platformRows.map((row) => `${row.idArticulo ?? ""}::${row.articulo ?? ""}`),
    );
    return keys.size;
  }, [platformRows]);

  const totals = useMemo(() => {
    const rowsToSum = filteredRows;
    const initial: Record<HourKey, number> = {
      hora00: 0,
      hora01: 0,
      hora02: 0,
      hora03: 0,
      hora04: 0,
      hora05: 0,
      hora06: 0,
      hora07: 0,
      hora08: 0,
      hora09: 0,
      hora10: 0,
      hora11: 0,
      hora12: 0,
      hora13: 0,
      hora14: 0,
      hora15: 0,
      hora16: 0,
      hora17: 0,
      hora18: 0,
      hora19: 0,
      hora20: 0,
      hora21: 0,
      hora22: 0,
      hora23: 0,
    };
    const initialMontos: Record<HourAmountKey, number> = {
      hora00Monto: 0,
      hora01Monto: 0,
      hora02Monto: 0,
      hora03Monto: 0,
      hora04Monto: 0,
      hora05Monto: 0,
      hora06Monto: 0,
      hora07Monto: 0,
      hora08Monto: 0,
      hora09Monto: 0,
      hora10Monto: 0,
      hora11Monto: 0,
      hora12Monto: 0,
      hora13Monto: 0,
      hora14Monto: 0,
      hora15Monto: 0,
      hora16Monto: 0,
      hora17Monto: 0,
      hora18Monto: 0,
      hora19Monto: 0,
      hora20Monto: 0,
      hora21Monto: 0,
      hora22Monto: 0,
      hora23Monto: 0,
    };

    let sinHora = 0;
    let sinHoraMonto = 0;
    let total = 0;
    let totalMonto = 0;

    rowsToSum.forEach((row) => {
      HOUR_KEYS.forEach((key) => {
        initial[key] += Number(row[key] ?? 0);
      });
      HOUR_AMOUNT_KEYS.forEach((key) => {
        initialMontos[key] += Number(row[key] ?? 0);
      });
      sinHora += Number(row.sinHora ?? 0);
      sinHoraMonto += Number(row.sinHoraMonto ?? 0);
      total += Number(row.total ?? 0);
      totalMonto += Number(row.totalMonto ?? 0);
    });

    return {
      ...initial,
      ...initialMontos,
      sinHora,
      sinHoraMonto,
      total,
      totalMonto,
    };
  }, [filteredRows]);

  const buildHourTotalsForRows = useCallback((rowsToSum: ConsumoPorHoraRow[]): HourTotals => {
    const initial: Record<HourKey, number> = {
      hora00: 0,
      hora01: 0,
      hora02: 0,
      hora03: 0,
      hora04: 0,
      hora05: 0,
      hora06: 0,
      hora07: 0,
      hora08: 0,
      hora09: 0,
      hora10: 0,
      hora11: 0,
      hora12: 0,
      hora13: 0,
      hora14: 0,
      hora15: 0,
      hora16: 0,
      hora17: 0,
      hora18: 0,
      hora19: 0,
      hora20: 0,
      hora21: 0,
      hora22: 0,
      hora23: 0,
    };
    const initialMontos: Record<HourAmountKey, number> = {
      hora00Monto: 0,
      hora01Monto: 0,
      hora02Monto: 0,
      hora03Monto: 0,
      hora04Monto: 0,
      hora05Monto: 0,
      hora06Monto: 0,
      hora07Monto: 0,
      hora08Monto: 0,
      hora09Monto: 0,
      hora10Monto: 0,
      hora11Monto: 0,
      hora12Monto: 0,
      hora13Monto: 0,
      hora14Monto: 0,
      hora15Monto: 0,
      hora16Monto: 0,
      hora17Monto: 0,
      hora18Monto: 0,
      hora19Monto: 0,
      hora20Monto: 0,
      hora21Monto: 0,
      hora22Monto: 0,
      hora23Monto: 0,
    };

    let sinHora = 0;
    let sinHoraMonto = 0;
    let total = 0;
    let totalMonto = 0;

    rowsToSum.forEach((row) => {
      HOUR_KEYS.forEach((key) => {
        initial[key] += Number(row[key] ?? 0);
      });
      HOUR_AMOUNT_KEYS.forEach((key) => {
        initialMontos[key] += Number(row[key] ?? 0);
      });
      sinHora += Number(row.sinHora ?? 0);
      sinHoraMonto += Number(row.sinHoraMonto ?? 0);
      total += Number(row.total ?? 0);
      totalMonto += Number(row.totalMonto ?? 0);
    });

    return {
      ...initial,
      ...initialMontos,
      sinHora,
      sinHoraMonto,
      total,
      totalMonto,
    };
  }, []);

  const hourSections = useMemo(() => {
    if (groupViewMode !== "coleccion") {
      return [{ key: "all", label: null as string | null, rows: filteredRows }];
    }

    const rowsByCollection = collectionGroups.map(() => [] as ConsumoPorHoraRow[]);
    const unassigned: ConsumoPorHoraRow[] = [];

    filteredRows.forEach((row) => {
      const id = String(row.idArticulo ?? "").trim();
      if (!id) {
        unassigned.push(row);
        return;
      }
      let assigned = false;
      for (let index = 0; index < collectionGroups.length; index += 1) {
        if (collectionGroups[index].articulos.has(id)) {
          rowsByCollection[index].push(row);
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        unassigned.push(row);
      }
    });

    const sections: Array<{ key: string; label: string | null; rows: ConsumoPorHoraRow[] }> = [];
    collectionGroups.forEach((collection, index) => {
      const rowsInCollection = rowsByCollection[index];
      if (rowsInCollection.length === 0) return;
      sections.push({
        key: `collection-${collection.id}`,
        label: collection.nombre,
        rows: rowsInCollection,
      });
    });

    if (unassigned.length > 0 || collectionGroups.length === 0) {
      sections.push({
        key: "collection-none",
        label: "Sin colección",
        rows: unassigned,
      });
    }

    return sections;
  }, [groupViewMode, filteredRows, collectionGroups]);

  const buildPlatformTotalsForRows = useCallback(
    (
      rowsToSum: Array<{
        values: Record<string, { cantidad: number; monto: number }>;
        total: number;
        totalMonto: number;
      }>,
    ) => {
      const totalsByPlatform: Record<string, { cantidad: number; monto: number }> = {};
      platformColumns.forEach((column) => {
        totalsByPlatform[column] = { cantidad: 0, monto: 0 };
      });

      let grandTotal = 0;
      let grandTotalMonto = 0;
      rowsToSum.forEach((row) => {
        platformColumns.forEach((column) => {
          const value = row.values[column] ?? { cantidad: 0, monto: 0 };
          totalsByPlatform[column].cantidad += Number(value.cantidad ?? 0);
          totalsByPlatform[column].monto += Number(value.monto ?? 0);
        });
        grandTotal += Number(row.total ?? 0);
        grandTotalMonto += Number(row.totalMonto ?? 0);
      });

      return { totalsByPlatform, grandTotal, grandTotalMonto };
    },
    [platformColumns],
  );

  const platformSections = useMemo(() => {
    if (groupViewMode !== "coleccion") {
      return [{ key: "all", label: null as string | null, rows: platformMatrix.rows }];
    }

    const rowsByCollection = collectionGroups.map(
      () => [] as typeof platformMatrix.rows,
    );
    const unassigned: typeof platformMatrix.rows = [];

    platformMatrix.rows.forEach((row) => {
      const id = String(row.idArticulo ?? "").trim();
      if (!id) {
        unassigned.push(row);
        return;
      }
      let assigned = false;
      for (let index = 0; index < collectionGroups.length; index += 1) {
        if (collectionGroups[index].articulos.has(id)) {
          rowsByCollection[index].push(row);
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        unassigned.push(row);
      }
    });

    const sections: Array<{ key: string; label: string | null; rows: typeof platformMatrix.rows }> = [];
    collectionGroups.forEach((collection, index) => {
      const rowsInCollection = rowsByCollection[index];
      if (rowsInCollection.length === 0) return;
      sections.push({
        key: `collection-${collection.id}`,
        label: collection.nombre,
        rows: rowsInCollection,
      });
    });

    if (unassigned.length > 0 || collectionGroups.length === 0) {
      sections.push({
        key: "collection-none",
        label: "Sin colección",
        rows: unassigned,
      });
    }
    return sections;
  }, [groupViewMode, platformMatrix.rows, collectionGroups]);

  const emptyHourRanges = useMemo<EmptyHourRange[]>(() => {
    const hasDataByHour = HOUR_KEYS.map((hourKey) =>
      filteredRows.some((row) => {
        const qty = Number(row[hourKey] ?? 0);
        const amount = Number(row[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0);
        return qty !== 0 || amount !== 0;
      }),
    );

    const ranges: EmptyHourRange[] = [];
    let index = 0;
    while (index < HOUR_KEYS.length) {
      if (hasDataByHour[index]) {
        index += 1;
        continue;
      }
      let end = index + 1;
      while (end < HOUR_KEYS.length && !hasDataByHour[end]) {
        end += 1;
      }
      if (end - index > 1) {
        const keys = HOUR_KEYS.slice(index, end);
        const rangeKey = `${keys[0]}-${keys[keys.length - 1]}`;
        ranges.push({
          rangeKey,
          keys,
          label: `${String(index).padStart(2, "0")}-${String(end - 1).padStart(2, "0")}`,
        });
      }
      index = end;
    }

    return ranges;
  }, [filteredRows]);

  const hourDisplayColumns = useMemo<HourDisplayColumn[]>(() => {
    const rangesByStart = new Map<HourKey, EmptyHourRange>();
    emptyHourRanges.forEach((range) => {
      const start = range.keys[0];
      if (start) {
        rangesByStart.set(start, range);
      }
    });

    const columns: HourDisplayColumn[] = [];
    let index = 0;
    while (index < HOUR_KEYS.length) {
      const hourKey = HOUR_KEYS[index];
      const range = rangesByStart.get(hourKey);
      if (range) {
        const expanded = Boolean(expandedEmptyHourRanges[range.rangeKey]);
        if (expanded) {
          range.keys.forEach((key, keyIndex) => {
            columns.push({
              type: "hour",
              key,
              label: getHourLabel(key),
              rangeKey: range.rangeKey,
              rangeLabel: range.label,
              isRangeStart: keyIndex === 0,
            });
          });
        } else {
          columns.push({
            type: "collapsed",
            rangeKey: range.rangeKey,
            keys: range.keys,
            label: range.label,
          });
        }
        index += range.keys.length;
        continue;
      }
      columns.push({
        type: "hour",
        key: hourKey,
        label: getHourLabel(hourKey),
      });
      index += 1;
    }

    return columns;
  }, [emptyHourRanges, expandedEmptyHourRanges]);

  const toggleEmptyHourRange = useCallback((rangeKey: string) => {
    setExpandedEmptyHourRanges((prev) => ({
      ...prev,
      [rangeKey]: !prev[rangeKey],
    }));
  }, []);

  const tableColSpan = hourDisplayColumns.length + 4;

  const actionTabsForCurrentView = useMemo<ActionsModalTab[]>(() => {
    if (viewMode === "Por Hora") {
      const tabs: ActionsModalTab[] = hourDisplayColumns.map((column) =>
        column.type === "hour"
          ? {
              key: `hour-${column.key}`,
              label: column.label,
              filter: {
                type: "hour",
                from: Number(column.key.slice(4)),
                to: Number(column.key.slice(4)),
              },
            }
          : {
              key: `hour-${column.rangeKey}`,
              label: column.label,
              filter: {
                type: "hour",
                from: Number(column.keys[0].slice(4)),
                to: Number(column.keys[column.keys.length - 1].slice(4)),
              },
            },
      );
      tabs.push({
        key: "hour-sin-hora",
        label: "Sin hora",
        filter: { type: "hour", from: -1, to: -1 },
      });
      return tabs;
    }
    return platformColumns.map((column, index) => ({
      key: `dim-${index}-${column}`,
      label: column,
      filter: { type: "dimension", value: column },
    }));
  }, [viewMode, hourDisplayColumns, platformColumns]);

  const handleOpenActionsModal = useCallback(
    (ingredientId: string | null, ingredientLabel: string) => {
      const tabs = actionTabsForCurrentView;
      setActionsModalViewMode(viewMode);
      setActionsModalIngredientId((ingredientId ?? "").trim());
      setActionsModalIngredient(ingredientLabel);
      setActionsModalTabs(tabs);
      setActionsModalActiveTab(tabs[0]?.key ?? "");
      setActionsModalRowsByTab({});
      setActionsModalErrorByTab({});
      setActionsModalLoadingByTab({});
      setActionsModalOpen(true);
    },
    [actionTabsForCurrentView, viewMode],
  );

  const handleCloseActionsModal = useCallback(() => {
    setActionsModalOpen(false);
  }, []);

  const handleOpenTicketModal = useCallback(
    (row: ConsumoDetalleRow) => {
      const rows = Array.isArray(row.tickets) ? row.tickets : [];
      setTicketModalTitle(
        row.articuloVenta ?? row.idArticuloVenta ?? "Sin artículo vendido",
      );
      setTicketModalRows(rows);
      setTicketModalOpen(true);
    },
    [],
  );

  const handleCloseTicketModal = useCallback(() => {
    setTicketModalOpen(false);
  }, []);

  const handleOpenTicketDetailModal = useCallback(
    async (ticketRow: TicketModalRow) => {
      if (!serverSeleccionado || !dateFilter.periodo) {
        setTicketDetailModalTitle(ticketRow.ticket || "Sin ticket");
        setTicketDetailModalLoading(false);
        setTicketDetailModalData(null);
        setTicketDetailModalError("Faltan filtros para reconstruir el ticket.");
        setTicketDetailModalOpen(true);
        return;
      }

      if (ticketRow.idMovimiento === null || ticketRow.idMovimiento === undefined) {
        setTicketDetailModalTitle(ticketRow.ticket || "Sin ticket");
        setTicketDetailModalLoading(false);
        setTicketDetailModalData(null);
        setTicketDetailModalError(
          "Este registro no tiene Id movimiento para reconstruir la factura.",
        );
        setTicketDetailModalOpen(true);
        return;
      }

      setTicketDetailModalTitle(
        ticketRow.ticket || `MOV-${String(ticketRow.idMovimiento)}`,
      );
      setTicketDetailModalData(null);
      setTicketDetailModalError(null);
      setTicketDetailModalOpen(true);
      setTicketDetailModalLoading(true);

      try {
        const params = new URLSearchParams({
          server: serverSeleccionado,
          periodo: dateFilter.periodo,
          fechaDesde: dateFilter.fechaDesde,
          fechaHasta: dateFilter.fechaHasta,
          idMovimiento: String(ticketRow.idMovimiento),
        });
        if (ticketRow.ticket?.trim()) {
          params.set("ticket", ticketRow.ticket.trim());
        }

        const data = await requestJson<ConsumoTicketReconstruidoResponse>(
          `/api/informes/ventas/consumo/ticket?${params.toString()}`,
        );
        setTicketDetailModalData(data);
      } catch (err) {
        setTicketDetailModalData(null);
        setTicketDetailModalError(
          err instanceof Error
            ? err.message
            : "No se pudo reconstruir el ticket seleccionado.",
        );
      } finally {
        setTicketDetailModalLoading(false);
      }
    },
    [
      dateFilter.fechaDesde,
      dateFilter.fechaHasta,
      dateFilter.periodo,
      serverSeleccionado,
    ],
  );

  const handleCloseTicketDetailModal = useCallback(() => {
    setTicketDetailModalLoading(false);
    setTicketDetailModalOpen(false);
  }, []);

  useEffect(() => {
    if (!actionsModalOpen || actionsModalTabs.length === 0) {
      return;
    }
    if (!serverSeleccionado || !dateFilter.periodo) {
      const fallbackError = "Faltan filtros para cargar el detalle.";
      const errorMap: Record<string, string | null> = {};
      const loadingMap: Record<string, boolean> = {};
      const rowsMap: Record<string, ConsumoDetalleRow[]> = {};
      actionsModalTabs.forEach((tab) => {
        errorMap[tab.key] = fallbackError;
        loadingMap[tab.key] = false;
        rowsMap[tab.key] = [];
      });
      setActionsModalRowsByTab(rowsMap);
      setActionsModalErrorByTab(errorMap);
      setActionsModalLoadingByTab(loadingMap);
      return;
    }

    let active = true;
    const initialLoading: Record<string, boolean> = {};
    const initialErrors: Record<string, string | null> = {};
    const initialRows: Record<string, ConsumoDetalleRow[]> = {};
    actionsModalTabs.forEach((tab) => {
      initialLoading[tab.key] = true;
      initialErrors[tab.key] = null;
      initialRows[tab.key] = [];
    });
    setActionsModalLoadingByTab(initialLoading);
    setActionsModalErrorByTab(initialErrors);
    setActionsModalRowsByTab(initialRows);

    const loadTabDetails = async (tab: ActionsModalTab) => {
      try {
        const params = new URLSearchParams({
          server: serverSeleccionado,
          periodo: dateFilter.periodo,
          fechaDesde: dateFilter.fechaDesde,
          fechaHasta: dateFilter.fechaHasta,
          modo: CONSUMO_DETAIL_MODE_BY_VIEW[actionsModalViewMode],
          ingrediente: actionsModalIngredient,
        });
        if (actionsModalIngredientId) {
          params.set("idIngrediente", actionsModalIngredientId);
        }
        if (sucursalSeleccionada) {
          params.set("sucursal", sucursalSeleccionada);
        }

        if (tab.filter.type === "hour") {
          params.set("horaDesde", String(tab.filter.from));
          params.set("horaHasta", String(tab.filter.to));
        } else {
          params.set("dimension", tab.filter.value);
        }

        const data = await requestJson<ConsumoDetalleRow[]>(
          `/api/informes/ventas/consumo/detalle?${params.toString()}`,
        );
        if (!active) return;
        setActionsModalRowsByTab((prev) => ({
          ...prev,
          [tab.key]: Array.isArray(data)
            ? data.map((row) => ({
                ...row,
                tickets: Array.isArray(row.tickets) ? row.tickets : [],
              }))
            : [],
        }));
        setActionsModalErrorByTab((prev) => ({
          ...prev,
          [tab.key]: null,
        }));
      } catch (err) {
        if (!active) return;
        setActionsModalRowsByTab((prev) => ({
          ...prev,
          [tab.key]: [],
        }));
        setActionsModalErrorByTab((prev) => ({
          ...prev,
          [tab.key]:
            err instanceof Error ? err.message : "No se pudo cargar el detalle.",
        }));
      } finally {
        if (active) {
          setActionsModalLoadingByTab((prev) => ({
            ...prev,
            [tab.key]: false,
          }));
        }
      }
    };

    actionsModalTabs.forEach((tab) => {
      void loadTabDetails(tab);
    });

    return () => {
      active = false;
    };
  }, [
    actionsModalOpen,
    actionsModalTabs,
    actionsModalViewMode,
    actionsModalIngredient,
    actionsModalIngredientId,
    serverSeleccionado,
    sucursalSeleccionada,
    dateFilter.periodo,
    dateFilter.fechaDesde,
    dateFilter.fechaHasta,
  ]);

  const renderActionsButton = useCallback(
    (onClick: () => void) => (
      <div className="d-flex justify-content-center">
        <button
          type="button"
          className="btn btn-sm btn-soft-primary py-0 px-2"
          title="Ver movimientos"
          onClick={onClick}
        >
          <i className="ri-search-eye-line"></i>
        </button>
      </div>
    ),
    [],
  );

  const buildDetailTotals = useCallback(
    (detailRows: ConsumoDetalleRow[]): ConsumoDetalleTotals =>
      detailRows.reduce<ConsumoDetalleTotals>(
        (acc, row) => {
          acc.cantidadVendida += Number(row.cantidadVendida ?? 0);
          acc.cantidadConsumida += Number(row.cantidadConsumida ?? 0);
          acc.montoConsumido += Number(row.montoConsumido ?? 0);
          acc.cantidadMovimientos += Number(row.cantidadMovimientos ?? 0);
          return acc;
        },
        {
          cantidadVendida: 0,
          cantidadConsumida: 0,
          montoConsumido: 0,
          cantidadMovimientos: 0,
        },
      ),
    [],
  );

  const renderMetricValue = useCallback(
    (
      cantidad: number,
      monto: number,
      options: { emphasized?: boolean; fullAmount?: boolean } = {},
    ) => {
      const qty = Number(cantidad ?? 0);
      const amount = Number(monto ?? 0);
      const isZero = qty === 0 && amount === 0;
      const baseClass = options.emphasized ? "fw-semibold" : "";
      const amountText = options.fullAmount
        ? formatCost(amount)
        : formatCostCompact(amount);

      if (metricMode === "cantidad") {
        return <span className={baseClass}>{isZero ? "—" : formatNumber(qty)}</span>;
      }
      if (metricMode === "monto") {
        return <span className={baseClass}>{isZero ? "—" : amountText}</span>;
      }
      if (isZero) {
        return <span className={baseClass}>—</span>;
      }
      return (
        <div className={`lh-sm ${baseClass}`}>
          <div>{formatNumber(qty)}</div>
          <small className="text-muted">{amountText}</small>
        </div>
      );
    },
    [metricMode],
  );

  const getHeatValue = useCallback(
    (row: ConsumoPorHoraRow, hourKey: HourKey) => {
      if (metricMode === "monto") {
        return Number(row[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0);
      }
      return Number(row[hourKey] ?? 0);
    },
    [metricMode],
  );

  const buildHeatCellStyle = useCallback(
    (intensity: number): React.CSSProperties => {
      if (intensity <= 0) {
        return HOUR_CELL_STYLE;
      }
      const clamped = Math.max(0, Math.min(1, intensity));
      const minAlpha = isDarkTheme ? 0.18 : 0.1;
      const maxAlpha = isDarkTheme ? 0.65 : 0.45;
      const alpha = minAlpha + (maxAlpha - minAlpha) * clamped;
      const accentRgb = isDarkTheme ? "var(--bs-info-rgb)" : "var(--bs-primary-rgb)";
      return {
        ...HOUR_CELL_STYLE,
        backgroundColor: `rgba(${accentRgb}, ${alpha.toFixed(3)})`,
        boxShadow:
          clamped >= 0.999
            ? `inset 0 0 0 1px rgba(${accentRgb}, 0.95)`
            : undefined,
        transition: "background-color 120ms ease",
      };
    },
    [isDarkTheme],
  );

  const metricModeBadge = useMemo(() => {
    if (metricMode === "monto") {
      return {
        label: "Montos costo",
        className: "bg-success-subtle text-success",
      };
    }
    if (metricMode === "ambos") {
      return {
        label: "Cantidad + Monto",
        className: "bg-info-subtle text-info",
      };
    }
    return {
      label: "Cantidades",
      className: "bg-primary-subtle text-primary",
    };
  }, [metricMode]);

  const activeRecordsShown =
    viewMode !== "Por Hora"
      ? platformMatrix.rows.length
      : filteredRows.length;
  const activeRecordsTotal =
    viewMode !== "Por Hora"
      ? platformUniqueTotal
      : rows.length;
  const exportDisabled = !serverSeleccionado || !dateFilter.periodo || loadingRows;

  const getCollectionLabelForArticle = useCallback(
    (idArticulo: string | null | undefined) => {
      const id = String(idArticulo ?? "").trim();
      if (!id) {
        return "Sin colección";
      }
      const collectionId = collectionAssignmentMap.get(id);
      if (!collectionId) {
        return "Sin colección";
      }
      return collectionLabelMap.get(collectionId) ?? "Colección";
    },
    [collectionAssignmentMap, collectionLabelMap],
  );

  const toFileSafe = useCallback((value: string) => {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }, []);

  const metricModeLabel = useMemo(() => {
    if (metricMode === "monto") {
      return "Montos costo";
    }
    if (metricMode === "ambos") {
      return "Cantidad + Monto";
    }
    return "Cantidades";
  }, [metricMode]);

  const formatMetricCellText = useCallback(
    (cantidad: number, monto: number) => {
      const qtyValue = Number(cantidad ?? 0);
      const amountValue = Number(monto ?? 0);
      if (metricMode === "cantidad") {
        return qtyValue === 0 ? "—" : formatNumber(qtyValue);
      }
      if (metricMode === "monto") {
        return amountValue === 0 ? "—" : formatCost(amountValue);
      }
      if (qtyValue === 0 && amountValue === 0) {
        return "—";
      }
      return `${formatNumber(qtyValue)} / ${formatCost(amountValue)}`;
    },
    [metricMode],
  );

  const filterHourRowsForExport = useCallback(
    (sourceRows: ConsumoPorHoraRow[]) => {
      const query = normalizeSearchText(searchTerm);
      const tokens = query.split(/\s+/).filter((item) => item.length > 0);

      return sourceRows.filter((row) => {
        const articleId = String(row.idArticulo ?? "").trim();
        const collectionId = articleId ? collectionAssignmentMap.get(articleId) : undefined;
        const collectionLabel = collectionId
          ? collectionLabelMap.get(collectionId) ?? ""
          : "Sin colección";
        const matchesCollection =
          collectionFilter === "todas" ||
          (collectionFilter === NULL_COLLECTION_VALUE
            ? !collectionId
            : collectionId === collectionFilter);
        if (!matchesCollection) {
          return false;
        }
        if (tokens.length === 0) {
          return true;
        }
        const haystack = normalizeSearchText(
          [
            row.articulo ?? "",
            row.idArticulo ?? "",
            row.familia ?? "",
            row.linea ?? "",
            row.marca ?? "",
            collectionLabel,
          ].join(" "),
        );
        return tokens.every((token) => haystack.includes(token));
      });
    },
    [collectionAssignmentMap, collectionFilter, collectionLabelMap, searchTerm],
  );

  const filterDimensionRowsForExport = useCallback(
    (sourceRows: ConsumoPorDimensionRow[]) => {
      const query = normalizeSearchText(searchTerm);
      const tokens = query.split(/\s+/).filter((item) => item.length > 0);

      return sourceRows.filter((row) => {
        const articleId = String(row.idArticulo ?? "").trim();
        const collectionId = articleId ? collectionAssignmentMap.get(articleId) : undefined;
        const collectionLabel = collectionId
          ? collectionLabelMap.get(collectionId) ?? ""
          : "Sin colección";
        const matchesCollection =
          collectionFilter === "todas" ||
          (collectionFilter === NULL_COLLECTION_VALUE
            ? !collectionId
            : collectionId === collectionFilter);
        if (!matchesCollection) {
          return false;
        }
        if (tokens.length === 0) {
          return true;
        }
        const haystack = normalizeSearchText(
          [
            row.articulo ?? "",
            row.idArticulo ?? "",
            row.familia ?? "",
            row.linea ?? "",
            row.marca ?? "",
            row.dimension ?? "",
            collectionLabel,
          ].join(" "),
        );
        return tokens.every((token) => haystack.includes(token));
      });
    },
    [collectionAssignmentMap, collectionFilter, collectionLabelMap, searchTerm],
  );

  const buildDimensionMatrixForExport = useCallback(
    (
      sourceRows: ConsumoPorDimensionRow[],
      mode: ConsumoDimensionMode,
    ): ConsumoExportDimensionDataset => {
      const grouped = new Map<string, ConsumoDimensionMatrixRow>();
      const uniqueColumns = new Set<string>();
      sourceRows.forEach((row) => {
        const idArticulo = row.idArticulo ? String(row.idArticulo) : null;
        const articulo = row.articulo ?? idArticulo ?? "Sin ingrediente";
        const groupKey = `${idArticulo ?? ""}::${articulo}`;
        const dimension = String(row.dimension ?? "").trim();
        uniqueColumns.add(dimension);

        const cantidad = Number(row.cantidad ?? 0);
        const monto = Number(row.monto ?? 0);

        let current = grouped.get(groupKey);
        if (!current) {
          current = {
            idArticulo,
            articulo,
            values: {},
            total: 0,
            totalMonto: 0,
          };
          grouped.set(groupKey, current);
        }

        const slot = current.values[dimension] ?? { cantidad: 0, monto: 0 };
        slot.cantidad += cantidad;
        slot.monto += monto;
        current.values[dimension] = slot;
        current.total += cantidad;
        current.totalMonto += monto;
      });

      const columns = Array.from(uniqueColumns);
      if (mode === "Por Día semana") {
        columns.sort((a, b) => {
          const rankA = getDayOfWeekRank(a);
          const rankB = getDayOfWeekRank(b);
          if (rankA !== rankB) return rankA - rankB;
          return a.localeCompare(b, "es");
        });
      } else {
        columns.sort((a, b) => a.localeCompare(b, "es"));
      }

      const matrixRows = Array.from(grouped.values()).sort((a, b) => {
        const totalDiff = b.total - a.total;
        if (totalDiff !== 0) return totalDiff;
        return a.articulo.localeCompare(b.articulo, "es");
      });

      const totalsByDimension: Record<string, { cantidad: number; monto: number }> = {};
      columns.forEach((column) => {
        totalsByDimension[column] = { cantidad: 0, monto: 0 };
      });
      let grandTotal = 0;
      let grandTotalMonto = 0;

      matrixRows.forEach((row) => {
        columns.forEach((column) => {
          const value = row.values[column] ?? { cantidad: 0, monto: 0 };
          totalsByDimension[column].cantidad += Number(value.cantidad ?? 0);
          totalsByDimension[column].monto += Number(value.monto ?? 0);
        });
        grandTotal += Number(row.total ?? 0);
        grandTotalMonto += Number(row.totalMonto ?? 0);
      });

      return {
        kind: "dimension",
        mode,
        rows: matrixRows,
        columns,
        totalsByDimension,
        grandTotal,
        grandTotalMonto,
      };
    },
    [],
  );

  const fetchExportDatasetForView = useCallback(
    async (targetMode: ConsumoViewMode): Promise<ConsumoExportDataset> => {
      const params = new URLSearchParams({
        server: serverSeleccionado,
        periodo: dateFilter.periodo,
        fechaDesde: dateFilter.fechaDesde,
        fechaHasta: dateFilter.fechaHasta,
      });
      if (sucursalSeleccionada) {
        params.set("sucursal", sucursalSeleccionada);
      }

      if (targetMode === "Por Hora") {
        const sourceRows =
          viewMode === "Por Hora"
            ? rows
            : await requestJson<ConsumoPorHoraRow[]>(
                `/api/informes/ventas/consumo/hora?${params.toString()}`,
              );
        const filtered = filterHourRowsForExport(
          Array.isArray(sourceRows) ? sourceRows : [],
        );
        return {
          kind: "hour",
          mode: "Por Hora",
          rows: filtered,
          totals: buildHourTotalsForRows(filtered),
        };
      }

      const dimensionMode = targetMode as ConsumoDimensionMode;
      const emptyLabel = CONSUMO_DIMENSION_EMPTY_LABEL[dimensionMode];
      const sourceRows =
        viewMode !== "Por Hora" && viewMode === dimensionMode
          ? platformRows
          : await requestJson<ConsumoPorDimensionRow[]>(
              `${CONSUMO_DIMENSION_ENDPOINT[dimensionMode]}?${params.toString()}`,
            );
      const normalizedRows = (Array.isArray(sourceRows) ? sourceRows : []).map((row) => {
        const rawDimension = String(row.dimension ?? "").trim();
        return {
          ...row,
          dimension: rawDimension.length > 0 ? rawDimension : emptyLabel,
        };
      });
      const filtered = filterDimensionRowsForExport(normalizedRows);
      return buildDimensionMatrixForExport(filtered, dimensionMode);
    },
    [
      buildDimensionMatrixForExport,
      buildHourTotalsForRows,
      dateFilter.fechaDesde,
      dateFilter.fechaHasta,
      dateFilter.periodo,
      filterDimensionRowsForExport,
      filterHourRowsForExport,
      platformRows,
      rows,
      serverSeleccionado,
      sucursalSeleccionada,
      viewMode,
    ],
  );

  const collectExportDatasets = useCallback(
    async (target: ConsumoExportTarget): Promise<ConsumoExportDataset[]> => {
      const modes = target === "all" ? CONSUMO_VIEW_MODE_OPTIONS : [target];
      const datasets = await Promise.all(
        modes.map((mode) => fetchExportDatasetForView(mode)),
      );
      return datasets.filter((dataset) => dataset.rows.length > 0);
    },
    [fetchExportDatasetForView],
  );

  const toSheetName = useCallback((mode: ConsumoViewMode) => {
    const byMode: Record<ConsumoViewMode, string> = {
      "Por Hora": "PorHora",
      "Por Plataforma": "PorPlataforma",
      "Por Marca": "PorMarca",
      "Por Sucursal": "PorSucursal",
      "Por Día semana": "PorDiaSemana",
      "Por Plan venta": "PorPlanVenta",
    };
    const raw = (byMode[mode] ?? toFileSafe(mode)) || "Vista";
    return raw.slice(0, 31);
  }, [toFileSafe]);

  const buildExcelRowsForDataset = useCallback(
    (dataset: ConsumoExportDataset) => {
      if (dataset.kind === "hour") {
        const sheetRows = dataset.rows.map((row) => {
          const base: Record<string, string | number> = {
            ID_INGREDIENTE: row.idArticulo ?? "",
            INGREDIENTE: row.articulo ?? row.idArticulo ?? "Sin ingrediente",
            COLECCION: getCollectionLabelForArticle(row.idArticulo),
          };

          HOUR_KEYS.forEach((hourKey) => {
            const hourLabel = getHourLabel(hourKey);
            const qty = Number(row[hourKey] ?? 0);
            const amount = Number(row[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0);
            if (metricMode === "cantidad") {
              base[hourLabel] = qty;
            } else if (metricMode === "monto") {
              base[hourLabel] = amount;
            } else {
              base[`${hourLabel} CANT`] = qty;
              base[`${hourLabel} MONTO`] = amount;
            }
          });

          if (metricMode === "cantidad") {
            base.SIN_HORA = Number(row.sinHora ?? 0);
            base.TOTAL = Number(row.total ?? 0);
          } else if (metricMode === "monto") {
            base.SIN_HORA = Number(row.sinHoraMonto ?? 0);
            base.TOTAL = Number(row.totalMonto ?? 0);
          } else {
            base["SIN_HORA CANT"] = Number(row.sinHora ?? 0);
            base["SIN_HORA MONTO"] = Number(row.sinHoraMonto ?? 0);
            base["TOTAL CANT"] = Number(row.total ?? 0);
            base["TOTAL MONTO"] = Number(row.totalMonto ?? 0);
          }
          return base;
        });

        const totalsRow: Record<string, string | number> = {
          ID_INGREDIENTE: "",
          INGREDIENTE: "TOTAL",
          COLECCION: "",
        };
        HOUR_KEYS.forEach((hourKey) => {
          const hourLabel = getHourLabel(hourKey);
          const qty = Number(dataset.totals[hourKey] ?? 0);
          const amount = Number(dataset.totals[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0);
          if (metricMode === "cantidad") {
            totalsRow[hourLabel] = qty;
          } else if (metricMode === "monto") {
            totalsRow[hourLabel] = amount;
          } else {
            totalsRow[`${hourLabel} CANT`] = qty;
            totalsRow[`${hourLabel} MONTO`] = amount;
          }
        });
        if (metricMode === "cantidad") {
          totalsRow.SIN_HORA = Number(dataset.totals.sinHora ?? 0);
          totalsRow.TOTAL = Number(dataset.totals.total ?? 0);
        } else if (metricMode === "monto") {
          totalsRow.SIN_HORA = Number(dataset.totals.sinHoraMonto ?? 0);
          totalsRow.TOTAL = Number(dataset.totals.totalMonto ?? 0);
        } else {
          totalsRow["SIN_HORA CANT"] = Number(dataset.totals.sinHora ?? 0);
          totalsRow["SIN_HORA MONTO"] = Number(dataset.totals.sinHoraMonto ?? 0);
          totalsRow["TOTAL CANT"] = Number(dataset.totals.total ?? 0);
          totalsRow["TOTAL MONTO"] = Number(dataset.totals.totalMonto ?? 0);
        }
        return [...sheetRows, totalsRow];
      }

      const sheetRows = dataset.rows.map((row) => {
        const base: Record<string, string | number> = {
          ID_INGREDIENTE: row.idArticulo ?? "",
          INGREDIENTE: row.articulo ?? row.idArticulo ?? "Sin ingrediente",
          COLECCION: getCollectionLabelForArticle(row.idArticulo),
        };

        dataset.columns.forEach((column) => {
          const value = row.values[column] ?? { cantidad: 0, monto: 0 };
          if (metricMode === "cantidad") {
            base[column] = Number(value.cantidad ?? 0);
          } else if (metricMode === "monto") {
            base[column] = Number(value.monto ?? 0);
          } else {
            base[`${column} CANT`] = Number(value.cantidad ?? 0);
            base[`${column} MONTO`] = Number(value.monto ?? 0);
          }
        });

        if (metricMode === "cantidad") {
          base.TOTAL = Number(row.total ?? 0);
        } else if (metricMode === "monto") {
          base.TOTAL = Number(row.totalMonto ?? 0);
        } else {
          base["TOTAL CANT"] = Number(row.total ?? 0);
          base["TOTAL MONTO"] = Number(row.totalMonto ?? 0);
        }
        return base;
      });

      const totalsRow: Record<string, string | number> = {
        ID_INGREDIENTE: "",
        INGREDIENTE: "TOTAL",
        COLECCION: "",
      };
      dataset.columns.forEach((column) => {
        const value = dataset.totalsByDimension[column] ?? { cantidad: 0, monto: 0 };
        if (metricMode === "cantidad") {
          totalsRow[column] = Number(value.cantidad ?? 0);
        } else if (metricMode === "monto") {
          totalsRow[column] = Number(value.monto ?? 0);
        } else {
          totalsRow[`${column} CANT`] = Number(value.cantidad ?? 0);
          totalsRow[`${column} MONTO`] = Number(value.monto ?? 0);
        }
      });
      if (metricMode === "cantidad") {
        totalsRow.TOTAL = Number(dataset.grandTotal ?? 0);
      } else if (metricMode === "monto") {
        totalsRow.TOTAL = Number(dataset.grandTotalMonto ?? 0);
      } else {
        totalsRow["TOTAL CANT"] = Number(dataset.grandTotal ?? 0);
        totalsRow["TOTAL MONTO"] = Number(dataset.grandTotalMonto ?? 0);
      }

      return [...sheetRows, totalsRow];
    },
    [getCollectionLabelForArticle, metricMode],
  );

  const handleExportExcel = useCallback(
    async (target: ConsumoExportTarget = viewMode) => {
      if (exportDisabled) return;

      try {
        const datasets = await collectExportDatasets(target);
        if (datasets.length === 0) {
          return;
        }

        const workbook = XLSX.utils.book_new();
        datasets.forEach((dataset) => {
          const rowsForSheet = buildExcelRowsForDataset(dataset);
          if (rowsForSheet.length === 0) return;
          const worksheet = XLSX.utils.json_to_sheet(rowsForSheet);
          XLSX.utils.book_append_sheet(workbook, worksheet, toSheetName(dataset.mode));
        });

        if (workbook.SheetNames.length === 0) {
          return;
        }

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        const targetName = target === "all" ? "todo" : toFileSafe(target);
        XLSX.writeFile(
          workbook,
          `ventas-consumo-${targetName}-${toFileSafe(metricMode)}-${timestamp}.xlsx`,
        );
      } catch (err) {
        console.error("No se pudo exportar a Excel:", err);
        setError("No se pudo generar el Excel del informe.");
      }
    },
    [
      buildExcelRowsForDataset,
      collectExportDatasets,
      exportDisabled,
      metricMode,
      toFileSafe,
      toSheetName,
      viewMode,
    ],
  );

  const handleExportPdf = useCallback(
    async (target: ConsumoExportTarget = viewMode) => {
      if (exportDisabled) return;

      try {
        const datasets = await collectExportDatasets(target);
        if (datasets.length === 0) {
          return;
        }

        const requiresA3 = datasets.some((dataset) => dataset.kind === "hour");
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "pt",
          format: requiresA3 ? "a3" : "a4",
        });

        const fechaHora = new Date().toLocaleString("es-PY");
        const usuarioActual = resolveUsuarioActual();
        const labelServidor = serverSeleccionado || "Sin seleccionar";
        const labelPeriodo = dateFilter.label || "-";
        const labelSucursal = sucursalSeleccionada || "Todas las sucursales";
        const labelVista = target === "all" ? "Todas las vistas" : target;

        const leftX = 40;
        const rightX = 20;
        const pageWidth = doc.internal.pageSize.getWidth();
        const infoRightX = pageWidth - rightX;
        const infoMaxValueWidth = 180;
        const logoWidth = 110;
        const logoHeight = 24;
        const headerTopY = 24;
        const titleY = logoDataUrl ? headerTopY + logoHeight + 10 : headerTopY + 16;
        const titleBottomY = titleY + 35;
        const infoStartY = headerTopY + 4;
        const infoRowHeight = 11;
        const infoLines: Array<{ label: string; value: string }> = [
          { label: "Periodo", value: labelPeriodo },
          { label: "Sucursal", value: labelSucursal },
          { label: "Vista", value: labelVista },
          { label: "Modo", value: metricModeLabel },
          { label: "Colección", value: selectedCollectionLabel },
        ];
        const infoBottomY = infoStartY + (infoLines.length - 1) * infoRowHeight + 2;
        const headerContentBottom = Math.max(titleBottomY, infoBottomY);
        const headerHeight = headerContentBottom + 10;
        const tableMarginLeft = leftX;
        const tableMarginRight = rightX;

        const fitTextToWidth = (text: string, maxWidth: number) => {
          if (maxWidth <= 0) return "";
          const raw = String(text ?? "");
          if (doc.getTextWidth(raw) <= maxWidth) {
            return raw;
          }
          let value = raw;
          while (value.length > 0 && doc.getTextWidth(`${value}...`) > maxWidth) {
            value = value.slice(0, -1);
          }
          return value ? `${value}...` : "...";
        };

        const renderInfoLine = (y: number, label: string, value: string) => {
          doc.setFontSize(8);
          const labelText = `${label}: `;
          const valueText = fitTextToWidth(value, infoMaxValueWidth);
          const labelWidth = doc.getTextWidth(labelText);
          const valueWidth = doc.getTextWidth(valueText);
          const startX = infoRightX - (labelWidth + valueWidth);
          doc.setTextColor(0, 0, 0);
          doc.text(labelText, startX, y);
          doc.setTextColor(120, 120, 120);
          doc.text(valueText, startX + labelWidth, y);
        };

        const renderHeader = () => {
          if (logoDataUrl) {
            doc.addImage(logoDataUrl, "PNG", leftX, headerTopY - 4, logoWidth, logoHeight);
          }
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(14);
          doc.text("Ventas por consumo", leftX, titleY);
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.text("Generado: ", leftX, titleY + 13);
          doc.setTextColor(120, 120, 120);
          doc.text(fechaHora, leftX + doc.getTextWidth("Generado: "), titleY + 13);
          doc.setTextColor(0, 0, 0);
          doc.text("Usuario: ", leftX, titleY + 24);
          doc.setTextColor(120, 120, 120);
          doc.text(usuarioActual, leftX + doc.getTextWidth("Usuario: "), titleY + 24);
          doc.setTextColor(0, 0, 0);
          doc.text("Servidor: ", leftX, titleY + 35);
          doc.setTextColor(120, 120, 120);
          doc.text(labelServidor, leftX + doc.getTextWidth("Servidor: "), titleY + 35);
          infoLines.forEach((item, index) => {
            renderInfoLine(infoStartY + index * infoRowHeight, item.label, item.value);
          });
          doc.setTextColor(0, 0, 0);
        };

        const totalRowStyle = {
          fontStyle: "bold",
          fillColor: [33, 37, 41] as [number, number, number],
          textColor: [255, 255, 255] as [number, number, number],
        };

        const tableSpecs = datasets.map((dataset) => {
          if (dataset.kind === "hour") {
            const headers = [
              "Ingrediente",
              ...HOUR_KEYS.map((hourKey) => getHourLabel(hourKey)),
              "Sin hora",
              "Total",
            ];
            const body: any[] = dataset.rows.map((row) => {
              const ingredient = `${row.idArticulo ?? "-"} · ${
                row.articulo ?? row.idArticulo ?? "Sin ingrediente"
              }`;
              const values = HOUR_KEYS.map((hourKey) =>
                formatMetricCellText(
                  Number(row[hourKey] ?? 0),
                  Number(row[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0),
                ),
              );
              return [
                ingredient,
                ...values,
                formatMetricCellText(
                  Number(row.sinHora ?? 0),
                  Number(row.sinHoraMonto ?? 0),
                ),
                formatMetricCellText(Number(row.total ?? 0), Number(row.totalMonto ?? 0)),
              ];
            });
            const totalValues = HOUR_KEYS.map((hourKey) =>
              formatMetricCellText(
                Number(dataset.totals[hourKey] ?? 0),
                Number(dataset.totals[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0),
              ),
            );
            const totalRow = [
              "TOTAL",
              ...totalValues,
              formatMetricCellText(
                Number(dataset.totals.sinHora ?? 0),
                Number(dataset.totals.sinHoraMonto ?? 0),
              ),
              formatMetricCellText(
                Number(dataset.totals.total ?? 0),
                Number(dataset.totals.totalMonto ?? 0),
              ),
            ];
            body.push(
              totalRow.map((cell) => ({
                content: String(cell ?? ""),
                styles: totalRowStyle,
              })),
            );
            const columnStyles: Record<number, { halign: "left" | "right" }> = {
              0: { halign: "left" },
            };
            for (let index = 1; index < headers.length; index += 1) {
              columnStyles[index] = { halign: "right" };
            }
            return {
              title: `Ventas por consumo - ${dataset.mode}`,
              head: [headers],
              body,
              columnStyles,
              fontSize: 6,
            };
          }

          const headers = ["Ingrediente", ...dataset.columns, "Total"];
          const body: any[] = dataset.rows.map((row) => {
            const ingredient = `${row.idArticulo ?? "-"} · ${
              row.articulo ?? row.idArticulo ?? "Sin ingrediente"
            }`;
            const values = dataset.columns.map((column) => {
              const value = row.values[column] ?? { cantidad: 0, monto: 0 };
              return formatMetricCellText(
                Number(value.cantidad ?? 0),
                Number(value.monto ?? 0),
              );
            });
            return [
              ingredient,
              ...values,
              formatMetricCellText(Number(row.total ?? 0), Number(row.totalMonto ?? 0)),
            ];
          });
          const totalValues = dataset.columns.map((column) => {
            const value = dataset.totalsByDimension[column] ?? { cantidad: 0, monto: 0 };
            return formatMetricCellText(
              Number(value.cantidad ?? 0),
              Number(value.monto ?? 0),
            );
          });
          const totalRow = [
            "TOTAL",
            ...totalValues,
            formatMetricCellText(
              Number(dataset.grandTotal ?? 0),
              Number(dataset.grandTotalMonto ?? 0),
            ),
          ];
          body.push(
            totalRow.map((cell) => ({
              content: String(cell ?? ""),
              styles: totalRowStyle,
            })),
          );
          const columnStyles: Record<number, { halign: "left" | "right" }> = {
            0: { halign: "left" },
          };
          for (let index = 1; index < headers.length; index += 1) {
            columnStyles[index] = { halign: "right" };
          }
          return {
            title: `Ventas por consumo - ${dataset.mode}`,
            head: [headers],
            body,
            columnStyles,
            fontSize: 7,
          };
        });

        tableSpecs.forEach((spec, index) => {
          if (index > 0) {
            doc.addPage();
          }
          const titleYTable = headerHeight + 10;
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text(spec.title, tableMarginLeft, titleYTable);
          autoTable(doc, {
            head: spec.head,
            body: spec.body,
            startY: titleYTable + 6,
            margin: {
              top: headerHeight,
              left: tableMarginLeft,
              right: tableMarginRight,
            },
            styles: {
              fontSize: spec.fontSize,
              cellPadding: 2.5,
              overflow: "linebreak",
            },
            headStyles: { fillColor: [33, 37, 41] },
            theme: "grid",
            columnStyles: spec.columnStyles,
            didDrawPage: () => {
              renderHeader();
            },
          });
        });

        const totalPages = doc.getNumberOfPages();
        for (let page = 1; page <= totalPages; page += 1) {
          doc.setPage(page);
          const pageWidthCurrent = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.text(`Página ${page} de ${totalPages}`, pageWidthCurrent - 30, pageHeight - 20, {
            align: "right",
          });
        }

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        const targetName = target === "all" ? "todo" : toFileSafe(target);
        doc.save(`ventas-consumo-${targetName}-${toFileSafe(metricMode)}-${timestamp}.pdf`);
      } catch (err) {
        console.error("No se pudo exportar a PDF:", err);
        setError("No se pudo generar el PDF del informe.");
      }
    },
    [
      collectExportDatasets,
      dateFilter.label,
      exportDisabled,
      formatMetricCellText,
      logoDataUrl,
      metricMode,
      metricModeLabel,
      selectedCollectionLabel,
      serverSeleccionado,
      sucursalSeleccionada,
      toFileSafe,
      viewMode,
    ],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <style>{`
          .ventas-consumo-sticky-table thead th {
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: var(--vz-secondary-bg, var(--vz-light));
          }
          .ventas-consumo-sticky-table thead th:first-child {
            left: 0;
            z-index: 11;
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .ventas-consumo-sticky-table tbody td:first-child:not([colspan]) {
            position: sticky;
            left: 0;
            z-index: 6;
            background-color: var(--vz-body-bg);
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .ventas-consumo-sticky-table tbody tr.table-light td:first-child:not([colspan]) {
            background-color: var(--vz-secondary-bg, var(--vz-light));
          }
          .ventas-consumo-modal-table thead th {
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: var(--vz-secondary-bg, var(--vz-light));
            font-size: 0.925rem !important;
            line-height: 1.05 !important;
            padding: 0.42rem 0.55rem !important;
          }
          .ventas-consumo-modal-table thead th:first-child {
            left: 0;
            z-index: 11;
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .ventas-consumo-modal-table tbody td,
          .ventas-consumo-modal-table tfoot th,
          .ventas-consumo-modal-table tfoot td {
            font-size: 0.925rem !important;
            line-height: 1.06 !important;
            padding: 0.42rem 0.55rem !important;
          }
          .ventas-consumo-modal-table tbody td:first-child:not([colspan]) {
            position: sticky;
            left: 0;
            z-index: 6;
            background-color: var(--vz-body-bg);
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
          .ventas-consumo-modal-table tfoot th:first-child,
          .ventas-consumo-modal-table tfoot td:first-child {
            position: sticky;
            left: 0;
            z-index: 7;
            background-color: var(--vz-secondary-bg, var(--vz-light));
            box-shadow: 1px 0 0 var(--vz-border-color);
          }
        `}</style>
        <BreadCrumb title="Ventas consumo" pageTitle="Informes" />

        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-transparent">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex flex-column gap-2">
                <div>
                  <h5 className="mb-1">Ventas por consumo</h5>
                  <p className="text-muted mb-0">
                    {CONSUMO_VIEW_DESCRIPTION[viewMode]}
                  </p>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <ButtonGroup>
                    {CONSUMO_VIEW_MODE_OPTIONS.map((option) => (
                      <Button
                        key={option}
                        type="button"
                        color="dark"
                        className={`btn-soft-dark consumo-view-mode-btn mb-0 ${
                          viewMode === option ? "active" : ""
                        }`}
                        active={viewMode === option}
                        style={
                          viewMode === option && isDarkTheme
                            ? DARK_ACTIVE_VIEW_MODE_STYLE
                            : undefined
                        }
                        onClick={() => setViewMode(option)}
                      >
                        {option}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
              </div>

              <div className="d-flex align-items-end gap-2 flex-wrap justify-content-end">
                <Dropdown
                  isOpen={excelDropdownOpen}
                  toggle={() => setExcelDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-excel-2-line me-1"></i>
                    Excel
                  </DropdownToggle>
                  <DropdownMenu end>
                    {CONSUMO_VIEW_MODE_OPTIONS.map((modeOption) => (
                      <DropdownItem
                        key={`excel-${modeOption}`}
                        onClick={() => {
                          void handleExportExcel(modeOption);
                        }}
                      >
                        <i className="ri-file-list-3-line me-2"></i>
                        {modeOption}
                      </DropdownItem>
                    ))}
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() => {
                        void handleExportExcel("all");
                      }}
                    >
                      <i className="ri-file-copy-2-line me-2"></i>
                      Descargar todo
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Dropdown
                  isOpen={pdfDropdownOpen}
                  toggle={() => setPdfDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-pdf-2-line me-1"></i>
                    PDF
                  </DropdownToggle>
                  <DropdownMenu end>
                    {CONSUMO_VIEW_MODE_OPTIONS.map((modeOption) => (
                      <DropdownItem
                        key={`pdf-${modeOption}`}
                        onClick={() => {
                          void handleExportPdf(modeOption);
                        }}
                      >
                        <i className="ri-file-list-3-line me-2"></i>
                        {modeOption}
                      </DropdownItem>
                    ))}
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() => {
                        void handleExportPdf("all");
                      }}
                    >
                      <i className="ri-file-copy-2-line me-2"></i>
                      Descargar todo
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                {canViewCollections ? (
                  <Button
                    color="dark"
                    className="btn-soft-dark"
                    onClick={() => setIsCollectionsModalOpen(true)}
                  >
                    <i className="ri-settings-3-line me-1"></i>
                    Colecciones
                  </Button>
                ) : null}
                <Button
                  color="dark"
                  className="btn-soft-dark"
                  onClick={() => setFiltersOpen(true)}
                >
                  <i className="ri-equalizer-line me-1"></i>
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="d-flex flex-column">
                <small className="text-muted">
                  Servidor: {serverSeleccionado || "-"} · Período: {dateFilter.label || "-"}
                  {sucursalSeleccionada
                    ? ` · Sucursal: ${sucursalSeleccionada}`
                    : " · Todas las sucursales"}
                  {canViewCollections ? ` · Colección: ${selectedCollectionLabel}` : ""}
                  <span className={`badge ms-2 ${metricModeBadge.className}`}>
                    {metricModeBadge.label}
                  </span>
                </small>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {canViewCollections ? (
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ width: 170, maxWidth: "100%" }}
                    value={groupViewMode}
                    onChange={(event) =>
                      setGroupViewMode(event.target.value as "sin" | "coleccion")
                    }
                  >
                    <option value="sin">Sin colección</option>
                    <option value="coleccion">Por colección</option>
                  </Input>
                ) : null}
                <Input
                  type="search"
                  bsSize="sm"
                  style={{ width: 260, maxWidth: "100%" }}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar ingrediente, código, familia, línea o marca..."
                />
                <small className="text-muted">
                  Registros: {activeRecordsShown}
                  {searchTerm.trim().length > 0 ? ` / ${activeRecordsTotal}` : ""}
                </small>
              </div>
            </div>

            {error ? <Alert color="danger">{error}</Alert> : null}

            {viewMode === "Por Hora" ? (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 table-nowrap ventas-consumo-sticky-table">
                  <thead className="table-light">
                    <tr>
                      <th style={INGREDIENT_COLUMN_STYLE_HOUR}>Ingrediente</th>
                      {hourDisplayColumns.map((column) => {
                        if (column.type === "hour") {
                          return (
                            <th
                              key={column.key}
                              className="text-end"
                              style={column.isRangeStart ? COLLAPSED_HOUR_HEADER_STYLE : HOUR_COLUMN_STYLE}
                            >
                              {column.isRangeStart && column.rangeKey ? (
                                <div className="d-flex flex-column align-items-center gap-1">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary py-0 px-2 lh-1"
                                    title={`Contraer ${column.rangeLabel ?? "rango"}`}
                                    onClick={() => toggleEmptyHourRange(column.rangeKey as string)}
                                  >
                                    <i className="ri-subtract-line me-1"></i>
                                    <span>Contraer</span>
                                  </button>
                                  <small className="text-muted">
                                    {buildEmptyHoursLegend(column.rangeLabel ?? column.label)}
                                  </small>
                                </div>
                              ) : (
                                column.label
                              )}
                            </th>
                          );
                        }
                        return (
                          <th
                            key={column.rangeKey}
                            className="text-center"
                            style={COLLAPSED_HOUR_HEADER_STYLE}
                          >
                            <div className="d-flex flex-column align-items-center gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary py-0 px-2 lh-1"
                                title={`Expandir ${column.label}`}
                                onClick={() => toggleEmptyHourRange(column.rangeKey)}
                              >
                                <i className="ri-add-line me-1"></i>
                                <span>Expandir</span>
                              </button>
                              <small className="text-muted">
                                {buildEmptyHoursLegend(column.label)}
                              </small>
                            </div>
                          </th>
                        );
                      })}
                      <th className="text-end" style={SUMMARY_COLUMN_STYLE}>Sin hora</th>
                      <th className="text-end" style={SUMMARY_COLUMN_STYLE}>Total</th>
                      <th style={ACTION_COLUMN_STYLE}>ACC.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRows ? (
                      <tr>
                        <td colSpan={tableColSpan} className="text-center py-4 text-muted">
                          <Spinner size="sm" className="me-2" />
                          Cargando consumo por hora...
                        </td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={tableColSpan} className="text-center py-4 text-muted">
                          {searchTerm.trim().length > 0
                            ? "No hay resultados para la búsqueda actual."
                            : "No hay datos para los filtros seleccionados."}
                        </td>
                      </tr>
                    ) : (
                      <>
                        {hourSections.map((section) => {
                          const sectionTotals = buildHourTotalsForRows(section.rows);
                          return (
                            <React.Fragment key={section.key}>
                              {section.label ? (
                                <tr className="table-light text-muted">
                                  <td colSpan={tableColSpan} className="fw-semibold text-uppercase">
                                    {section.label}
                                  </td>
                                </tr>
                              ) : null}
                              {section.rows.map((row, index) => {
                                const key = `${section.key}-${row.idArticulo ?? "sin-id"}-${index}`;
                                const rowMaxHeatValue = HOUR_KEYS.reduce(
                                  (max, hourKey) => Math.max(max, getHeatValue(row, hourKey)),
                                  0,
                                );
                                return (
                                  <tr key={key}>
                                    <td style={INGREDIENT_CELL_STYLE_HOUR}>
                                      <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                                        {row.idArticulo ? (
                                          <span className="badge bg-primary">
                                            {row.idArticulo}
                                          </span>
                                        ) : null}
                                        <span
                                          className="fw-semibold"
                                          title={row.articulo ?? row.idArticulo ?? "Sin ingrediente"}
                                        >
                                          {truncateWithDots(
                                            row.articulo ?? row.idArticulo ?? "Sin ingrediente",
                                            28,
                                          )}
                                        </span>
                                      </div>
                                    </td>
                                    {hourDisplayColumns.map((column) => {
                                      if (column.type === "collapsed") {
                                        return (
                                          <td
                                            key={`${key}-${column.rangeKey}`}
                                            className="text-muted"
                                            style={COLLAPSED_HOUR_CELL_STYLE}
                                          >
                                            ...
                                          </td>
                                        );
                                      }

                                      const hourKey = column.key;
                                      return (
                                        <td
                                          key={`${key}-${hourKey}`}
                                          className="text-end"
                                          style={buildHeatCellStyle(
                                            rowMaxHeatValue > 0
                                              ? getHeatValue(row, hourKey) / rowMaxHeatValue
                                              : 0,
                                          )}
                                        >
                                          {renderMetricValue(
                                            Number(row[hourKey] ?? 0),
                                            Number(row[HOUR_TO_AMOUNT_KEY[hourKey]] ?? 0),
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="text-end" style={SUMMARY_CELL_STYLE}>
                                      {renderMetricValue(
                                        Number(row.sinHora ?? 0),
                                        Number(row.sinHoraMonto ?? 0),
                                      )}
                                    </td>
                                    <td className="text-end fw-semibold" style={SUMMARY_CELL_STYLE}>
                                      {renderMetricValue(
                                        Number(row.total ?? 0),
                                        Number(row.totalMonto ?? 0),
                                        { emphasized: true, fullAmount: true },
                                      )}
                                    </td>
                                    <td style={ACTION_CELL_STYLE}>
                                      {renderActionsButton(() =>
                                        handleOpenActionsModal(
                                          row.idArticulo ?? null,
                                          row.articulo ?? row.idArticulo ?? "Sin ingrediente",
                                        ),
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              {section.label ? (
                                <tr className="table-light fw-semibold">
                                  <td style={INGREDIENT_COLUMN_STYLE_HOUR}>
                                    Subtotal {section.label}
                                  </td>
                                  {hourDisplayColumns.map((column) => {
                                    if (column.type === "collapsed") {
                                      return (
                                        <td
                                          key={`subtotal-${section.key}-${column.rangeKey}`}
                                          className="text-muted"
                                          style={COLLAPSED_HOUR_CELL_STYLE}
                                        >
                                          ...
                                        </td>
                                      );
                                    }
                                    const hourKey = column.key;
                                    return (
                                      <td
                                        key={`subtotal-${section.key}-${hourKey}`}
                                        className="text-end"
                                        style={HOUR_CELL_STYLE}
                                      >
                                        {renderMetricValue(
                                          sectionTotals[hourKey],
                                          sectionTotals[HOUR_TO_AMOUNT_KEY[hourKey]],
                                          { emphasized: true },
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="text-end" style={SUMMARY_CELL_STYLE}>
                                    {renderMetricValue(
                                      sectionTotals.sinHora,
                                      sectionTotals.sinHoraMonto,
                                      { emphasized: true },
                                    )}
                                  </td>
                                  <td className="text-end" style={SUMMARY_CELL_STYLE}>
                                    {renderMetricValue(
                                      sectionTotals.total,
                                      sectionTotals.totalMonto,
                                      { emphasized: true, fullAmount: true },
                                    )}
                                  </td>
                                  <td style={ACTION_CELL_STYLE} className="text-muted">
                                    -
                                  </td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          );
                        })}

                        <tr className="table-light fw-semibold">
                          <td style={INGREDIENT_COLUMN_STYLE_HOUR}>Total</td>
                          {hourDisplayColumns.map((column) => {
                            if (column.type === "collapsed") {
                              return (
                                <td
                                  key={`total-${column.rangeKey}`}
                                  className="text-muted"
                                  style={COLLAPSED_HOUR_CELL_STYLE}
                                >
                                  ...
                                </td>
                              );
                            }
                            const hourKey = column.key;
                            return (
                              <td key={`total-${hourKey}`} className="text-end" style={HOUR_CELL_STYLE}>
                                {renderMetricValue(
                                  totals[hourKey],
                                  totals[HOUR_TO_AMOUNT_KEY[hourKey]],
                                  { emphasized: true },
                                )}
                              </td>
                            );
                          })}
                          <td className="text-end" style={SUMMARY_CELL_STYLE}>
                            {renderMetricValue(totals.sinHora, totals.sinHoraMonto, {
                              emphasized: true,
                            })}
                          </td>
                          <td className="text-end" style={SUMMARY_CELL_STYLE}>
                            {renderMetricValue(totals.total, totals.totalMonto, {
                              emphasized: true,
                              fullAmount: true,
                            })}
                          </td>
                          <td style={ACTION_CELL_STYLE} className="text-muted">
                            -
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 table-nowrap ventas-consumo-sticky-table">
                  <thead className="table-light">
                    <tr>
                      <th style={INGREDIENT_COLUMN_STYLE}>Ingrediente</th>
                      {platformColumns.map((platform) => (
                        <th
                          key={platform}
                          className="text-end"
                          style={HOUR_COLUMN_STYLE}
                          title={platform}
                        >
                          {platform}
                        </th>
                      ))}
                      <th className="text-end" style={SUMMARY_COLUMN_STYLE}>Total</th>
                      <th style={ACTION_COLUMN_STYLE}>ACC.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRows ? (
                      <tr>
                        <td colSpan={platformColumns.length + 3} className="text-center py-4 text-muted">
                          <Spinner size="sm" className="me-2" />
                          {`Cargando consumo por ${CONSUMO_LOADING_LABEL[viewMode as ConsumoDimensionMode]}...`}
                        </td>
                      </tr>
                    ) : platformMatrix.rows.length === 0 ? (
                      <tr>
                        <td colSpan={platformColumns.length + 3} className="text-center py-4 text-muted">
                          {searchTerm.trim().length > 0
                            ? "No hay resultados para la búsqueda actual."
                            : "No hay datos para los filtros seleccionados."}
                        </td>
                      </tr>
                    ) : (
                      <>
                        {platformSections.map((section) => {
                          const sectionTotals = buildPlatformTotalsForRows(section.rows);
                          return (
                            <React.Fragment key={section.key}>
                              {section.label ? (
                                <tr className="table-light text-muted">
                                  <td colSpan={platformColumns.length + 3} className="fw-semibold text-uppercase">
                                    {section.label}
                                  </td>
                                </tr>
                              ) : null}
                              {section.rows.map((row, index) => {
                                const key = `${section.key}-${row.idArticulo ?? "sin-id"}-${index}`;
                                return (
                                  <tr key={key}>
                                    <td style={INGREDIENT_CELL_STYLE}>
                                      <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                                        {row.idArticulo ? (
                                          <span className="badge bg-primary">
                                            {row.idArticulo}
                                          </span>
                                        ) : null}
                                        <span className="fw-semibold" title={row.articulo}>
                                          {truncateWithDots(row.articulo, 28)}
                                        </span>
                                      </div>
                                    </td>
                                    {platformColumns.map((platform) => {
                                      const value = row.values[platform] ?? {
                                        cantidad: 0,
                                        monto: 0,
                                      };
                                      return (
                                        <td
                                          key={`${key}-${platform}`}
                                          className="text-end"
                                          style={HOUR_CELL_STYLE}
                                        >
                                          {renderMetricValue(value.cantidad, value.monto)}
                                        </td>
                                      );
                                    })}
                                    <td className="text-end fw-semibold" style={SUMMARY_CELL_STYLE}>
                                      {renderMetricValue(row.total, row.totalMonto, {
                                        emphasized: true,
                                        fullAmount: true,
                                      })}
                                    </td>
                                    <td style={ACTION_CELL_STYLE}>
                                      {renderActionsButton(() =>
                                        handleOpenActionsModal(
                                          row.idArticulo ?? null,
                                          row.articulo ?? row.idArticulo ?? "Sin ingrediente",
                                        ),
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              {section.label ? (
                                <tr className="table-light fw-semibold">
                                  <td style={INGREDIENT_COLUMN_STYLE}>
                                    Subtotal {section.label}
                                  </td>
                                  {platformColumns.map((platform) => {
                                    const value = sectionTotals.totalsByPlatform[platform] ?? {
                                      cantidad: 0,
                                      monto: 0,
                                    };
                                    return (
                                      <td
                                        key={`subtotal-${section.key}-${platform}`}
                                        className="text-end"
                                        style={HOUR_CELL_STYLE}
                                      >
                                        {renderMetricValue(value.cantidad, value.monto, {
                                          emphasized: true,
                                        })}
                                      </td>
                                    );
                                  })}
                                  <td className="text-end" style={SUMMARY_CELL_STYLE}>
                                    {renderMetricValue(
                                      sectionTotals.grandTotal,
                                      sectionTotals.grandTotalMonto,
                                      {
                                        emphasized: true,
                                        fullAmount: true,
                                      },
                                    )}
                                  </td>
                                  <td style={ACTION_CELL_STYLE} className="text-muted">
                                    -
                                  </td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          );
                        })}

                        <tr className="table-light fw-semibold">
                          <td style={INGREDIENT_COLUMN_STYLE}>Total</td>
                          {platformColumns.map((platform) => {
                            const value = platformMatrix.totalsByPlatform[platform] ?? {
                              cantidad: 0,
                              monto: 0,
                            };
                            return (
                              <td key={`total-${platform}`} className="text-end" style={HOUR_CELL_STYLE}>
                                {renderMetricValue(value.cantidad, value.monto, {
                                  emphasized: true,
                                })}
                              </td>
                            );
                          })}
                          <td className="text-end" style={SUMMARY_CELL_STYLE}>
                            {renderMetricValue(
                              platformMatrix.grandTotal,
                              platformMatrix.grandTotalMonto,
                              {
                                emphasized: true,
                                fullAmount: true,
                              },
                            )}
                          </td>
                          <td style={ACTION_CELL_STYLE} className="text-muted">
                            -
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={actionsModalOpen}
          toggle={handleCloseActionsModal}
          size="xl"
          centered
        >
          <ModalHeader toggle={handleCloseActionsModal}>
            Acciones · {actionsModalViewMode}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <small className="text-muted d-block">Ingrediente</small>
              <div className="fw-semibold">
                {actionsModalIngredient || "Sin ingrediente"}
              </div>
            </div>

            {actionsModalTabs.length === 0 ? (
              <Alert color="light" className="mb-0">
                No hay columnas disponibles para mostrar tabs.
              </Alert>
            ) : (
              <>
                <Nav tabs className="mb-3 flex-nowrap overflow-auto">
                  {actionsModalTabs.map((tab) => (
                    <NavItem key={tab.key}>
                      {(() => {
                        const tabRows = actionsModalRowsByTab[tab.key] ?? [];
                        const tabLoading = Boolean(actionsModalLoadingByTab[tab.key]);
                        const tabError = actionsModalErrorByTab[tab.key];
                        const isNoData = !tabLoading && !tabError && tabRows.length === 0;
                        return (
                      <NavLink
                        href="#"
                        active={actionsModalActiveTab === tab.key}
                        disabled={isNoData}
                        className={isNoData ? "text-muted opacity-50" : undefined}
                        onClick={(event) => {
                          event.preventDefault();
                          if (isNoData) {
                            return;
                          }
                          setActionsModalActiveTab(tab.key);
                        }}
                      >
                        {tab.label}
                      </NavLink>
                        );
                      })()}
                    </NavItem>
                  ))}
                </Nav>

                <TabContent activeTab={actionsModalActiveTab}>
                  {actionsModalTabs.map((tab) => (
                    <TabPane tabId={tab.key} key={tab.key}>
                      {actionsModalActiveTab === tab.key ? (
                        actionsModalLoadingByTab[tab.key] ? (
                          <div className="text-muted py-3">
                            <Spinner size="sm" className="me-2" />
                            Cargando detalle de cálculo...
                          </div>
                        ) : actionsModalErrorByTab[tab.key] ? (
                          <Alert color="danger" className="mb-0">
                            {actionsModalErrorByTab[tab.key]}
                          </Alert>
                        ) : (actionsModalRowsByTab[tab.key] ?? []).length === 0 ? (
                          <Alert color="light" className="mb-0">
                            Sin datos para esta pestaña.
                          </Alert>
                        ) : (
                          <div className="table-responsive">
                            <Table hover className="align-middle mb-0 table-nowrap ventas-consumo-modal-table">
                              {(() => {
                                const tabRows = actionsModalRowsByTab[tab.key] ?? [];
                                const totals = buildDetailTotals(tabRows);
                                return (
                                  <>
                              <thead className="table-light">
                                <tr>
                                  <th style={MODAL_DETAIL_HEADER_STYLE}>Artículo vendido</th>
                                  <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                                    Cant. vendida
                                  </th>
                                  <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                                    Factor receta
                                  </th>
                                  <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                                    Consumo
                                  </th>
                                  <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                                    Monto costo
                                  </th>
                                  <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                                    Ticket
                                  </th>
                                  <th className="text-center" style={MODAL_DETAIL_HEADER_STYLE}>
                                    ACC.
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {tabRows.map((detail, index) => (
                                  <tr
                                    key={`${detail.idArticuloVenta ?? "sin-id"}-${index}`}
                                  >
                                    <td style={MODAL_DETAIL_ARTICLE_CELL_STYLE}>
                                      <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                                        {detail.idArticuloVenta ? (
                                          <span className="badge bg-primary">
                                            {detail.idArticuloVenta}
                                          </span>
                                        ) : null}
                                        <span
                                          className="fw-semibold"
                                          title={
                                            detail.articuloVenta ??
                                            detail.idArticuloVenta ??
                                            "Sin artículo"
                                          }
                                        >
                                          {truncateWithDots(
                                            detail.articuloVenta ??
                                              detail.idArticuloVenta ??
                                              "Sin artículo",
                                            44,
                                          )}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                      {formatNumber(detail.cantidadVendida)}
                                    </td>
                                    <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                      {formatNumber(detail.factorReceta)}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={MODAL_DETAIL_CELL_STYLE}
                                    >
                                      {formatNumber(detail.cantidadConsumida)}
                                    </td>
                                    <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                      {formatCost(detail.montoConsumido)}
                                    </td>
                                    <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                      {formatNumber(detail.cantidadMovimientos)}
                                    </td>
                                    <td className="text-center" style={MODAL_DETAIL_CELL_STYLE}>
                                      {renderActionsButton(() =>
                                        handleOpenTicketModal(detail),
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="table-light fw-semibold">
                                  <td style={MODAL_DETAIL_ARTICLE_CELL_STYLE}>Total</td>
                                  <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                    {formatNumber(totals.cantidadVendida)}
                                  </td>
                                  <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                    -
                                  </td>
                                  <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                    {formatNumber(totals.cantidadConsumida)}
                                  </td>
                                  <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                    {formatCost(totals.montoConsumido)}
                                  </td>
                                  <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                                    {formatNumber(totals.cantidadMovimientos)}
                                  </td>
                                  <td className="text-center" style={MODAL_DETAIL_CELL_STYLE}>
                                    -
                                  </td>
                                </tr>
                              </tfoot>
                                  </>
                                );
                              })()}
                            </Table>
                          </div>
                        )
                      ) : null}
                    </TabPane>
                  ))}
                </TabContent>
              </>
            )}
          </ModalBody>
        </Modal>

        <Modal
          isOpen={ticketModalOpen}
          toggle={handleCloseTicketModal}
          size="xl"
          centered
        >
          <ModalHeader toggle={handleCloseTicketModal}>
            Tickets · {ticketModalTitle}
          </ModalHeader>
          <ModalBody>
            {ticketModalRows.length === 0 ? (
              <Alert color="light" className="mb-0">
                Sin tickets/facturas para este registro.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 table-nowrap ventas-consumo-modal-table">
                  <thead className="table-light">
                    <tr>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Ticket / Factura</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Movimiento</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Fecha</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Hora</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Sucursal</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Cliente</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>RUC / Doc.</th>
                      <th style={MODAL_DETAIL_HEADER_STYLE}>Origen</th>
                      <th className="text-center" style={MODAL_DETAIL_HEADER_STYLE}>
                        ACC.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketModalRows.map((ticketRow, index) => (
                      <tr
                        key={`${ticketRow.idMovimiento ?? "sin-mov"}-${ticketRow.ticket}-${index}`}
                      >
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketRow.ticket || "SIN TICKET"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketRow.idMovimiento ?? "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketRow.fecha ? formatHeaderDate(ticketRow.fecha) : "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>{ticketRow.hora ?? "-"}</td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>{ticketRow.sucursal ?? "-"}</td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>{ticketRow.cliente ?? "-"}</td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>{ticketRow.rucDoc ?? "-"}</td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>{ticketRow.origen ?? "-"}</td>
                        <td className="text-center" style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketRow.idMovimiento === null ||
                          ticketRow.idMovimiento === undefined ? (
                            <span className="text-muted">-</span>
                          ) : (
                            renderActionsButton(() => {
                              void handleOpenTicketDetailModal(ticketRow);
                            })
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </ModalBody>
        </Modal>

        <Modal
          isOpen={ticketDetailModalOpen}
          toggle={handleCloseTicketDetailModal}
          size="xl"
          centered
        >
          <ModalHeader toggle={handleCloseTicketDetailModal}>
            Factura reconstruida · {ticketDetailModalTitle}
          </ModalHeader>
          <ModalBody>
            {ticketDetailModalLoading ? (
              <div className="text-muted py-3">
                <Spinner size="sm" className="me-2" />
                Reconstruyendo ticket...
              </div>
            ) : ticketDetailModalError ? (
              <Alert color="danger" className="mb-0">
                {ticketDetailModalError}
              </Alert>
            ) : !ticketDetailModalData ? (
              <Alert color="light" className="mb-0">
                Sin datos para el ticket seleccionado.
              </Alert>
            ) : (
              <>
                <div className="table-responsive mb-3">
                  <Table
                    hover
                    className="align-middle mb-0 table-nowrap ventas-consumo-modal-table"
                  >
                    <thead className="table-light">
                      <tr>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Ticket</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Movimiento</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Fecha</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Hora</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Sucursal</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Cliente</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>RUC / Doc.</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Origen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.ticket || "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.idMovimiento}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.fecha
                            ? formatHeaderDate(ticketDetailModalData.cabecera.fecha)
                            : "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.hora ?? "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.sucursal ?? "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.persona ?? "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.ruc ??
                            ticketDetailModalData.cabecera.documentoIdentidad ??
                            "-"}
                        </td>
                        <td style={MODAL_DETAIL_CELL_STYLE}>
                          {ticketDetailModalData.cabecera.origen ?? "-"}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3 text-muted">
                  <small>Servidor: {ticketDetailModalData.cabecera.serverOrigen || "-"}</small>
                  <small>Depósito: {ticketDetailModalData.cabecera.deposito ?? "-"}</small>
                  <small>Marca: {ticketDetailModalData.cabecera.marca ?? "-"}</small>
                  <small>Plan venta: {ticketDetailModalData.cabecera.planVenta ?? "-"}</small>
                </div>

                <div className="table-responsive">
                  <Table
                    hover
                    className="align-middle mb-0 table-nowrap ventas-consumo-modal-table"
                  >
                    <thead className="table-light">
                      <tr>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Artículo vendido</th>
                        <th style={MODAL_DETAIL_HEADER_STYLE}>Artículo padre</th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          Cant. mov.
                        </th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          Cant. comanda
                        </th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          Dif.
                        </th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          P. unit
                        </th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          Total
                        </th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          Costo unit
                        </th>
                        <th className="text-end" style={MODAL_DETAIL_HEADER_STYLE}>
                          Costo total
                        </th>
                        <th className="text-center" style={MODAL_DETAIL_HEADER_STYLE}>
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticketDetailModalData.detalle.length === 0 ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="text-center text-muted py-3"
                            style={MODAL_DETAIL_CELL_STYLE}
                          >
                            Sin detalle para esta factura.
                          </td>
                        </tr>
                      ) : (
                        ticketDetailModalData.detalle.map((detail, index) => (
                          <tr key={`${detail.idArticulo ?? "sin-id"}-${index}`}>
                            <td style={MODAL_DETAIL_ARTICLE_CELL_STYLE}>
                              <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                                {detail.idArticulo ? (
                                  <span className="badge bg-primary">{detail.idArticulo}</span>
                                ) : null}
                                <span
                                  className="fw-semibold"
                                  title={detail.articulo ?? detail.idArticulo ?? "Sin artículo"}
                                >
                                  {truncateWithDots(
                                    detail.articulo ?? detail.idArticulo ?? "Sin artículo",
                                    40,
                                  )}
                                </span>
                              </div>
                            </td>
                            <td style={MODAL_DETAIL_ARTICLE_CELL_STYLE}>
                              <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                                {detail.idArticuloPadre ? (
                                  <span className="badge bg-secondary">
                                    {detail.idArticuloPadre}
                                  </span>
                                ) : null}
                                <span
                                  className="fw-semibold"
                                  title={
                                    detail.articuloPadre ??
                                    detail.idArticuloPadre ??
                                    "Sin artículo padre"
                                  }
                                >
                                  {truncateWithDots(
                                    detail.articuloPadre ??
                                      detail.idArticuloPadre ??
                                      "Sin artículo padre",
                                    40,
                                  )}
                                </span>
                              </div>
                            </td>
                            <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                              {formatNumber(detail.cantidadMovimiento)}
                            </td>
                            <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                              {formatNumber(detail.cantidadComanda)}
                            </td>
                            <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                              {formatNumber(detail.diferenciaCantidad)}
                            </td>
                            <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                              {formatCost(detail.precioUnitario)}
                            </td>
                            <td className="text-end fw-semibold" style={MODAL_DETAIL_CELL_STYLE}>
                              {formatCost(detail.totalImporte)}
                            </td>
                            <td className="text-end lh-sm" style={MODAL_DETAIL_CELL_STYLE}>
                              <div>{formatCost(detail.costoUnitSinIva)}</div>
                              <small className="text-muted">
                                {formatCost(detail.costoUnitConIva)}
                              </small>
                            </td>
                            <td className="text-end lh-sm" style={MODAL_DETAIL_CELL_STYLE}>
                              <div>{formatCost(detail.costoTotalSinIva)}</div>
                              <small className="text-muted">
                                {formatCost(detail.costoTotalConIva)}
                              </small>
                            </td>
                            <td className="text-center" style={MODAL_DETAIL_CELL_STYLE}>
                              {detail.estadoRelacion === "SOLO_MOVIMIENTO" ? (
                                <span className="badge bg-warning-subtle text-warning">
                                  Solo mov.
                                </span>
                              ) : detail.estadoRelacion === "SOLO_COMANDA" ? (
                                <span className="badge bg-info-subtle text-info">
                                  Solo com.
                                </span>
                              ) : (
                                <span className="badge bg-success-subtle text-success">
                                  Rel.
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light fw-semibold">
                        <td style={MODAL_DETAIL_ARTICLE_CELL_STYLE}>
                          Total ({ticketDetailModalData.totales.items} ítems)
                        </td>
                        <td style={MODAL_DETAIL_ARTICLE_CELL_STYLE}>-</td>
                        <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                          {formatNumber(ticketDetailModalData.totales.cantidadMovimiento)}
                        </td>
                        <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                          {formatNumber(ticketDetailModalData.totales.cantidadComanda)}
                        </td>
                        <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                          {formatNumber(
                            ticketDetailModalData.totales.cantidadMovimiento -
                              ticketDetailModalData.totales.cantidadComanda,
                          )}
                        </td>
                        <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                          -
                        </td>
                        <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                          {formatCost(ticketDetailModalData.totales.totalImporte)}
                        </td>
                        <td className="text-end" style={MODAL_DETAIL_CELL_STYLE}>
                          -
                        </td>
                        <td className="text-end lh-sm" style={MODAL_DETAIL_CELL_STYLE}>
                          <div>{formatCost(ticketDetailModalData.totales.totalCostoSinIva)}</div>
                          <small className="text-muted">
                            {formatCost(ticketDetailModalData.totales.totalCostoConIva)}
                          </small>
                        </td>
                        <td className="text-center" style={MODAL_DETAIL_CELL_STYLE}>
                          -
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>

        <Offcanvas
          direction="end"
          isOpen={filtersOpen}
          toggle={() => setFiltersOpen(false)}
          scrollable
        >
          <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
            Filtros
          </OffcanvasHeader>
          <OffcanvasBody>
            <div className="d-flex flex-column gap-3">
              <div>
                <small className="text-muted d-block mb-1">Servidor</small>
                <Input
                  type="select"
                  bsSize="sm"
                  value={serverSeleccionado}
                  onChange={(event) => setServerSeleccionado(event.target.value)}
                  disabled={serverLoading || serverOptions.length === 0}
                >
                  {serverOptions.length === 0 ? (
                    <option value="" disabled>
                      Cargando servidores...
                    </option>
                  ) : null}
                  {serverOptions.map((server) => (
                    <option key={server} value={server}>
                      {server}
                    </option>
                  ))}
                </Input>
              </div>

              <div className="w-100">
                <FormGroup className="w-100 mb-0">
                  <Label className="text-muted text-uppercase small fw-semibold">
                    Periodo (día / semana / mes / rango)
                  </Label>
                  <Dropdown
                    className="input-group flex-nowrap"
                    isOpen={dateModeDropdownOpen}
                    toggle={toggleDateModeDropdown}
                  >
                    <DropdownToggle
                      as="button"
                      className="btn btn-light border arrow-none d-flex align-items-center gap-2"
                    >
                      <i className={`${currentModeOption.icon} text-primary`} />
                      <span>{currentModeOption.label}</span>
                    </DropdownToggle>
                    {renderDateModeInput()}
                    <DropdownMenu
                      as="ul"
                      className="list-unstyled w-100 dropdown-menu-list mb-0"
                    >
                      <SimpleBar style={{ maxHeight: "220px" }} className="px-3">
                        {DATE_MODE_OPTIONS.map((option) => (
                          <DropdownItem
                            as="li"
                            key={option.key}
                            onClick={() => handleModeChange(option.key)}
                            active={option.key === dateSelection.mode}
                            className="dropdown-item d-flex align-items-start gap-2"
                          >
                            <div className="flex-shrink-0">
                              <i className={`${option.icon} text-primary`} />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">{option.label}</span>
                                {option.key === dateSelection.mode ? (
                                  <i className="ri-check-line text-success ms-2" />
                                ) : null}
                              </div>
                              <small className="text-muted">
                                {option.description}
                              </small>
                            </div>
                          </DropdownItem>
                        ))}
                      </SimpleBar>
                    </DropdownMenu>
                  </Dropdown>
                </FormGroup>
              </div>

              <div>
                {canViewCollections ? (
                  <>
                    <small className="text-muted d-block mb-1">Vista de colección</small>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={groupViewMode}
                      onChange={(event) =>
                        setGroupViewMode(event.target.value as "sin" | "coleccion")
                      }
                    >
                      <option value="sin">Sin colección</option>
                      <option value="coleccion">Por colección</option>
                    </Input>
                  </>
                ) : null}
              </div>

              <div>
                <small className="text-muted d-block mb-1">Modo de vista</small>
                <Input
                  type="select"
                  bsSize="sm"
                  value={metricMode}
                  onChange={(event) =>
                    setMetricMode(event.target.value as MetricMode)
                  }
                >
                  <option value="cantidad">Cantidades</option>
                  <option value="monto">Montos costo (PYG)</option>
                  <option value="ambos">Cantidades + Montos</option>
                </Input>
              </div>

              {canViewCollections ? (
                <div>
                  <small className="text-muted d-block mb-1">Colección</small>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={collectionFilter}
                    onChange={(event) => setCollectionFilter(event.target.value)}
                    disabled={collectionGroups.length === 0}
                  >
                    <option value="todas">Todas las colecciones</option>
                    <option value={NULL_COLLECTION_VALUE}>Sin colección</option>
                    {collectionGroups.map((collection) => (
                      <option key={collection.id} value={String(collection.id)}>
                        {collection.nombre}
                      </option>
                    ))}
                  </Input>
                </div>
              ) : null}

              <div>
                <small className="text-muted d-block mb-1">Sucursal</small>
                <Input
                  type="select"
                  bsSize="sm"
                  value={sucursalSeleccionada}
                  onChange={(event) => setSucursalSeleccionada(event.target.value)}
                >
                  <option value="">Todas las sucursales</option>
                  {sucursalOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Input>
              </div>

              <small className="text-muted d-block">
                La vista <strong>Por Hora</strong> usa <code>hora2</code> de <code>info.ventas_movimientos</code>
                para distribuir el consumo de ingredientes por columna horaria.
              </small>
            </div>
          </OffcanvasBody>
        </Offcanvas>

        <CollectionsModal
          isOpen={isCollectionsModalOpen}
          onClose={() => setIsCollectionsModalOpen(false)}
          canView={canViewCollections}
          canEdit={canEditCollections}
          canClone={canCloneCollections}
        />
      </Container>
    </div>
  );
};

export default VentasConsumoOfflinePage;
